
import { Component, signal, inject, computed, OnInit, AfterViewInit, OnDestroy, ElementRef, ViewChild, effect, untracked, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IconComponent, IconName } from '../../../../shared/icons/icon.component';
import { FormsModule } from '@angular/forms';
import { DragDropModule, CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Router } from '@angular/router';
import { forkJoin, of, EMPTY, Observable } from 'rxjs';
import { catchError, finalize, switchMap, map } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { DashboardCreateDialogComponent } from '../../../../shared/components/dashboard/dashboard-create-dialog.component';
import { DashboardAddWidgetDialogComponent } from '../../../../shared/components/dashboard/dashboard-add-widget-dialog.component';
import { ApiService } from '../../../../core/services/api.service';
import {
  DatePickerComponent,
  DateRangePickerOutput,
  DateRangePickerValue,
} from '../../../../shared/components/date-picker/date-picker.component';

import {
  WidgetHostComponent,
  type WidgetHostInstanceEvent,
} from '../../../../shared/components/dashboard/widget-host.component';
// import { type EmbeddedReportKey } from '../../../../shared/components/dashboard/widget-registry';
import {
  CATALOG_PAGE_HINTS,
  resolveDashboardWidgetDisplayTitle,
  stripProductPrefix,
} from '../../../../shared/components/dashboard/dashboard-embed.helpers';

/**
 * Canonical / alias widget keys that should render as a TALL tile
 * (row-span-2) on the unified My Dashboard grid when their col-span is 8.
 *
 * Mirrors `MonthlyReport1Component.SECTION_COL_CLASSES`'s
 * `lg:row-span-2` hint on `TOP_CATEGORIES_BY_CONTRACTS`, so embedding the
 * card here keeps the same "wide chart on the left, 2 narrow cards stacked
 * on the right" packing the user just configured in the Customize Layout
 * popup. Other widgets are unaffected.
 */
const TALL_TILE_WIDGET_KEYS_AT_COLSPAN_8: ReadonlySet<string> = new Set([
  'chart_top_categories_contracts', // canonical card id (from MonthlyReport1CardId)
  'mr1_top_categories_contracts',   // backend alias widget_key
]);
import {
  CustomizeDashboardStateComponent,
  CustomizeDashboardPreviewTile,
  normalizeCustomizeColSpan,
} from './customize-dashboard-state/customize-dashboard-state.component';
import {
  getUnifiedDashboardTileColClass,
  resolveUnifiedWidgetColSpan,
} from '../../../../shared/components/dashboard/dashboard-layout-editor.helpers';
import {
  CustomizeDashboardStateDialogComponent,
  CustomizeDashboardStateDialogData,
  // CustomizeDashboardStateDialogApplyResult,
} from './customize-dashboard-state/customize-dashboard-state-dialog.component';


interface CustomizeDashboardStateDialogApplyResult {
  tiles: CustomizeDashboardPreviewTile[];
  mixed: boolean;
}
import {
  DashboardPageKey,
  MONTHLY_REPORT_1_CARD_CONFIG,
  MONTHLY_REPORT_2_SECTIONS,
  SOCIAL_PLATFORM_SECTIONS,
  SeoStatsSectionDef,
  CampaignAdsStatsSectionDef,
  IndustryCategorizationSectionDef,
  TrainingDashboardSectionDef,
  BusinessPerformanceReportSectionDef,
  ContractsReportSectionDef,
  SocialPlatformSectionId,
  SeoStatsSectionId,
  SeoOverviewSectionId,
  CampaignAdsStatsSectionId,
  IndustryCategorizationSectionId,
  TrainingDashboardSectionId,
  BusinessPerformanceReportSectionId,
  ContractsReportSectionId,
  LendeeDashboardSectionId,
  LendeeAnalyticsSectionId,
  CoolCreditDashboardSectionId,
  CoolCreditGrowthSectionId,
  DailyCronSectionId,
  OverdueCronSectionId,
  ArrangementCronOverviewSectionId,
  ARRANGEMENT_CRON_OVERVIEW_BACKEND_WIDGET_KEYS,
  ArrangementCronTrendsSectionId,
  ARRANGEMENT_CRON_TRENDS_BACKEND_WIDGET_KEYS,
  ArrangementCronAdminPerformanceSectionId,
  ARRANGEMENT_CRON_ADMIN_PERFORMANCE_BACKEND_WIDGET_KEYS,
  type SectionDef,
  type DashboardCardConfig,
} from '../../../../core/enums/dashboard-cards.enum';
import { DashboardLayoutService } from '../../../../core/services/dashboard-layout.service';
import { ToasterService } from '../../../../core/services/toaster.service';
import { IndividualToasterComponent } from '../../../../shared/components/loader/loader.component';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

const LA_TIMEZONE = 'America/Los_Angeles';

interface Widget {
  id: string;
  title: string;
  icon: IconName;
  visible: boolean;
  isPinned: boolean;
  category: string;
  placementId?: number | null;
  widgetApiId?: number | null;
  colSpan?: number | null;
  rowSpan?: number | null;
  position?: number;
  /**
   * Per-placement `custom_config` payload returned by the backend with
   * the widget. Persisted by the source page when the user pins the
   * card (e.g. SEO Overview stores `{ view: 'metaData' | 'googleAdsSummary' }`)
   * and forwarded down to the embedded report via the widget-host so it
   * can rehydrate the same UI state the user saw when pinning.
   */
  customConfig?: Record<string, any> | null;
}

interface VisibleWidgetTile extends Widget {
  /** Parent dashboard/page id (e.g. `api-page-1`). */
  dashboardId: string;
  /** Display name for the page this widget came from (used as the "category" tag inside each tile). */
  pageName: string;
  /** Resolved report slug for the unified grid `@switch`. */
  reportKey: EmbeddedReportKey | null;
  /** Numeric catalog page id when available. */
  pageId?: number | null;
  /** Catalog product this widget belongs to. */
  productId?: number | string | null;
  /** Stable key for `@for` track-by. */
  uniqueKey: string;
}

interface Dashboard {
  id: string;
  name: string;
  icon: IconName;
  isExpanded: boolean;
  widgets: Widget[];
  /** Numeric dashboard ID from the API. Required to call visibility PATCH. */
  dashboardApiId?: number | null;
  /** Catalog product id this dashboard/page belongs to. Sent to backend on Apply Layout. */
  productId?: number | string | null;
  initialWidgetsEmpty?: boolean;
}

interface ReportPage {
  id: string;
  pageId?: number;
  title: string;
  icon: IconName;
  route: string;
  color: string;
  reportKey?: EmbeddedReportKey;
}

/*'col-span' for every widget preview for mirroring or real preview */

const ON_SCREEN_COL_SPAN_BY_WIDGET_KEY: Readonly<Record<string, 4 | 6 | 8 | 12>> = {
  // ── Monthly Report 1 (mirrors MR1.SECTION_COL_SPAN)
  mr1_mom_active_businesses: 8,
  mr1_contract_types_overview: 4,
  mr1_top_categories_contracts: 8,
  mr1_enrolled_businesses: 4,
  mr1_contracts_created: 4,
  mr1_overall_contract_type_count: 6,
  mr1_top_10_businesses: 6,
  mr1_overall_business_detail: 12,
  // Canonical MR1 card ids (defensive — same widths)
  kpi_active_businesses_mom: 8,
  kpi_contract_types_overview: 4,
  chart_top_categories_contracts: 8,
  table_enrolled_businesses: 4,
  table_contracts_created: 4,
  chart_overall_contract_type_count: 6,
  chart_top_businesses_contracts: 6,
  table_business_contract_detail: 12,

  // ── Monthly Report 2 (mirrors MR2.SECTION_COL_SPAN — widget_keys already match the canonical `MonthlyReport2SectionId` enum values)
  mr2_stats_overview: 8,
  mr2_mom_activity: 8,
  mr2_training_status: 4,
  mr2_monthly_analysis: 12,
  mr2_enrolled_running: 6,
  mr2_contracts_running: 6,

  // ── Social Platform
  [SocialPlatformSectionId.OVERALL_STATS]: 12,
  [SocialPlatformSectionId.PLATFORM_PERFORMANCE]: 12,
  [SocialPlatformSectionId.VIEWS_TREND]: 12,
  [SocialPlatformSectionId.OVERALL_ANALYTICS]: 12,

  // ── SEO Stats
  [SeoStatsSectionId.STATS_OVERVIEW]: 12,
  [SeoStatsSectionId.CLICKS_CHART]: 12,
  [SeoStatsSectionId.KEYWORD_RANKING]: 6,
  [SeoStatsSectionId.AI_OVERVIEW]: 6,
  [SeoStatsSectionId.COUNTRY_PERFORMANCE]: 6,
  [SeoStatsSectionId.TOP_CITIES]: 6,
  [SeoStatsSectionId.REVIEWS_RATINGS]: 12,
  [SeoStatsSectionId.LATEST_REVIEWS]: 12,

  // ── SEO Overview
  [SeoOverviewSectionId.STATS_OVERVIEW]: 12,
  [SeoOverviewSectionId.ACTIVE_CAMPAIGNS]: 8,
  [SeoOverviewSectionId.PLATFORMS]: 4,
  [SeoOverviewSectionId.DEVICE]: 8,
  [SeoOverviewSectionId.GENDER]: 4,
  [SeoOverviewSectionId.TOP_CITIES]: 8,
  [SeoOverviewSectionId.AGE]: 4,

  // ── Campaign Ads Stats
  [CampaignAdsStatsSectionId.STATS_OVERVIEW]: 12,
  [CampaignAdsStatsSectionId.MOM_COMPARISON]: 12,
  [CampaignAdsStatsSectionId.MONTHLY_ENQUIRIES]: 12,

  // ── Industry Categorization
  [IndustryCategorizationSectionId.INDUSTRY_CATEGORIZATION]: 12,
  [IndustryCategorizationSectionId.CITY_CATEGORIZATION]: 12,

  // ── Training Dashboard
  [TrainingDashboardSectionId.STATS_OVERVIEW]: 12,
  [TrainingDashboardSectionId.PERFORMANCE_OVERVIEW]: 12,
  [TrainingDashboardSectionId.SOURCE_CHANNEL_PERFORMANCE]: 12,
  [TrainingDashboardSectionId.COMPLETION_BY_TYPE]: 12,
  [TrainingDashboardSectionId.CANCELLATION_LEAD_TIME]: 6,
  [TrainingDashboardSectionId.STATUS_DISTRIBUTION]: 6,
  [TrainingDashboardSectionId.ATTEMPTS_BEFORE_COMPLETION]: 6,
  [TrainingDashboardSectionId.UNREGISTERED_BOOKING_CONVERSION]: 6,
  [TrainingDashboardSectionId.POST_REGISTRATION_JOURNEY]: 6,
  [TrainingDashboardSectionId.TIME_TO_FIRST_CONTRACT]: 6,
  [TrainingDashboardSectionId.TOP_ADMINS]: 12,
  [TrainingDashboardSectionId.ADMIN_CONVERSION_TABLE]: 12,

  // ── Business Performance Report (canonical `vb_*` widget_key suffixes)
  vb_registered_today: 12,
  vb_active_business: 6,
  vb_inactive_business: 6,
  vb_business_activated: 6,
  vb_business_revived: 6,
  vb_business_stage: 12,
  vb_business_highlights: 12,
  vb_trending_industries: 12,
  vb_business_states: 12,
  vb_business_phase: 12,
  vb_active_businesses_timeline: 12,
  vb_cohort_group_activity: 12,

  // ── Contracts Report (canonical `vb_*` widget_key suffixes)
  vb_contracts_created_today: 12,
  vb_contract_status_timeline: 12,
  vb_state_wise_businesses: 12,
  vb_top_contracts_platforms: 6,
  vb_average_contract_value: 6,
  vb_enrollment_collected_trend: 4,
  vb_upfront_collected_trend: 4,
  vb_late_fee_collected_trend: 4,
  vb_payment_date_fee_trend: 4,
  vb_downpayment_collected: 4,
  vb_edit_contract: 4,
  vb_reversals_trend: 4,
  vb_refunds: 4,
  vb_late_fee_refunded: 4,
  vb_settled_contracts: 4,
  vb_contract_state_resolution: 8,
  vb_protected_status_pending: 4,
  vb_protected_contracts_trend: 4,
  vb_non_protected_contracts: 4,

  // ── Lendee Dashboard (product metrics — `le_ln_*` backend keys + `lend_*` section ids)
  le_ln_applications_pipeline: 4,
  le_ln_offers_responses: 4,
  le_ln_payment_plans: 4,
  le_ln_risk_matrix: 12,
  le_ln_operational_efficiency: 6,
  le_ln_kpi_metrics: 6,
  [LendeeDashboardSectionId.REQUESTS_CARD]: 4,
  [LendeeDashboardSectionId.OFFERS_CARD]: 4,
  [LendeeDashboardSectionId.PAYMENT_PLANS_CARD]: 4,
  [LendeeDashboardSectionId.RISK_ASSESSMENT]: 12,
  [LendeeDashboardSectionId.OPERATIONAL_EFFICIENCY]: 6,
  [LendeeDashboardSectionId.KEY_PERFORMANCE_METRICS]: 6,

  // ── Lendee Analytics Dashboard
  le_ln_operational_kpis: 12,
  le_ln_key_insights: 6,
  le_ln_recipient_conversion_funnel: 6,
  le_ln_request_offer_performance: 6,
  le_ln_signups_trend: 6,
  le_ln_request_status_summary: 6,
  le_ln_offer_outcome_analysis: 6,
  le_ln_overdue_collections_progress: 6,
  le_ln_partial_repayments_trend: 6,
  le_ln_receivables_completion_revenue: 12,
  le_ln_receivables_recovery: 6,
  le_ln_top_decline_reasons: 6,
  [LendeeAnalyticsSectionId.METRICS_OVERVIEW]: 12,
  [LendeeAnalyticsSectionId.KEY_INSIGHTS]: 6,
  [LendeeAnalyticsSectionId.RECIPIENT_FUNNEL]: 6,
  [LendeeAnalyticsSectionId.REQUEST_OFFER_PERFORMANCE]: 6,
  [LendeeAnalyticsSectionId.SIGNUPS_TREND]: 6,
  [LendeeAnalyticsSectionId.REQUEST_STATUS]: 6,
  [LendeeAnalyticsSectionId.OFFER_OUTCOME]: 6,
  [LendeeAnalyticsSectionId.OVERDUE_COLLECTIONS]: 6,
  [LendeeAnalyticsSectionId.PARTIAL_REPAYMENTS]: 6,
  [LendeeAnalyticsSectionId.RECEIVABLES_VS_REVENUE]: 12,
  [LendeeAnalyticsSectionId.RECEIVABLES_RECOVERY]: 6,
  [LendeeAnalyticsSectionId.DECLINE_REASONS]: 6,

  // ── Cool Credit Dashboard (`co_cc_*` backend keys + `cc_*` section ids)
  co_cc_business_growth: 4,
  co_cc_activation_calls: 4,
  co_cc_product_usage: 4,
  co_cc_monetization_revenue: 4,
  co_cc_plan_lifecycle: 4,
  co_cc_disputes: 4,
  [CoolCreditDashboardSectionId.BUSINESS_GROWTH]: 4,
  [CoolCreditDashboardSectionId.ACTIVATION_AND_CALLS]: 4,
  [CoolCreditDashboardSectionId.PRODUCT_USAGE]: 4,
  [CoolCreditDashboardSectionId.MONETIZATION_REVENUE]: 4,
  [CoolCreditDashboardSectionId.PLAN_LIFECYCLE]: 4,
  [CoolCreditDashboardSectionId.DISPUTES]: 4,

  // ── Cool Credit: Growth, Activation & Revenue (`co_ga_*` + `cc_gar_*`)
  co_ga_growth_revenue_counters: 12,
  co_ga_growth_key_insights: 6,
  co_ga_journey_dropoff_comparison: 6,
  co_ga_onboarding_funnel: 6,
  co_ga_revenue_by_plan: 6,
  co_ga_cbc_fetch_outcomes: 8,
  co_ga_payment_method_distribution: 4,
  co_ga_simulator_usage_distribution: 4,
  co_ga_disputes_calls_trend: 12,
  co_ga_call_source_distribution: 6,
  co_ga_plan_lifecycle_status: 6,
  co_ga_page_session_distribution: 12,
  [CoolCreditGrowthSectionId.STAT_CARDS]: 12,
  [CoolCreditGrowthSectionId.KEY_INSIGHTS]: 6,
  [CoolCreditGrowthSectionId.DROPOFF_COMPARISON]: 6,
  [CoolCreditGrowthSectionId.ONBOARDING_FUNNEL]: 6,
  [CoolCreditGrowthSectionId.REVENUE_CONTRIBUTION]: 6,
  [CoolCreditGrowthSectionId.CBC_EFFECT]: 8,
  [CoolCreditGrowthSectionId.PAYMENT_METHOD]: 4,
  [CoolCreditGrowthSectionId.SIMULATOR_USAGE]: 4,
  [CoolCreditGrowthSectionId.MONTHLY_DISPUTES_CALLS]: 12,
  [CoolCreditGrowthSectionId.CALL_SOURCE]: 6,
  [CoolCreditGrowthSectionId.PLAN_LIFECYCLE]: 6,
  [CoolCreditGrowthSectionId.PAGE_SESSION]: 12,

  // ── Daily Cron Payments (`de_dc_*` backend keys + `dc_*` section ids)
  de_dc_last_attempt: 6,
  de_dc_try_again_overview: 6,
  de_dc_payment_lifecycle: 12,
  de_dc_payment_analytics_stats: 12,
  de_dc_volume_trends: 6,
  de_dc_amount_trends: 6,
  de_dc_success_rate_trend: 8,
  de_dc_efficiency: 4,
  de_dc_retry_strategy_impact: 4,
  de_dc_retry_strategy_trends: 8,
  de_dc_contract_state_events: 12,
  [DailyCronSectionId.LAST_ATTEMPT]: 6,
  [DailyCronSectionId.TRY_AGAIN_OVERVIEW]: 6,
  [DailyCronSectionId.PAYMENT_LIFECYCLE]: 12,
  [DailyCronSectionId.PAYMENT_ANALYTICS_STATS]: 12,
  [DailyCronSectionId.VOLUME_TRENDS]: 6,
  [DailyCronSectionId.AMOUNT_TRENDS]: 6,
  [DailyCronSectionId.SUCCESS_RATE_TREND]: 8,
  [DailyCronSectionId.EFFICIENCY]: 4,
  [DailyCronSectionId.RETRY_STRATEGY_IMPACT]: 4,
  [DailyCronSectionId.RETRY_STRATEGY_TRENDS]: 8,
  [DailyCronSectionId.CONTRACT_STATE_EVENTS]: 12,

  // ── Overdue Cron Payments (`de_oc_*` backend keys + `oc_*` section ids)
  de_oc_recent_attempt_overview: 12,
  de_oc_analytics_summary: 12,
  de_oc_volume_trends: 6,
  de_oc_transaction_amounts: 6,
  de_oc_status_distribution: 4,
  de_oc_method_distribution: 4,
  de_oc_revenue_distribution: 4,
  de_oc_success_rate_trend: 12,
  de_oc_detailed_statistics: 12,
  [OverdueCronSectionId.RECENT_ATTEMPT_OVERVIEW]: 12,
  [OverdueCronSectionId.ANALYTICS_SUMMARY]: 12,
  [OverdueCronSectionId.VOLUME_TRENDS]: 6,
  [OverdueCronSectionId.TRANSACTION_AMOUNTS]: 6,
  [OverdueCronSectionId.STATUS_DISTRIBUTION]: 4,
  [OverdueCronSectionId.METHOD_DISTRIBUTION]: 4,
  [OverdueCronSectionId.REVENUE_DISTRIBUTION]: 4,
  [OverdueCronSectionId.SUCCESS_RATE_TREND]: 12,
  [OverdueCronSectionId.DETAILED_STATISTICS]: 12,

  // ── Arrangement Cron — Overview Table (`de_ac_*` backend keys + `ac_*` section ids)
  de_ac_system_intel: 12,
  de_ac_scheduled_today: 6,
  de_ac_recent_attempts: 6,
  de_ac_kpi_summary: 12,
  de_ac_time_gap_distribution: 12,
  de_ac_quick_action: 12,
  de_ac_ar_aging_overview: 12,
  de_ac_monthly_distribution: 6,
  de_ac_creation_lifecycle: 6,
  de_ac_contract_status_chart: 12,
  de_ac_metric_anomalies: 12,
  de_ac_admin_duplicates_table: 12,
  [ArrangementCronOverviewSectionId.SYSTEM_INTEL]: 12,
  [ArrangementCronOverviewSectionId.SCHEDULED_TODAY]: 6,
  [ArrangementCronOverviewSectionId.RECENT_ATTEMPTS]: 6,
  [ArrangementCronOverviewSectionId.KPI_SUMMARY]: 12,
  [ArrangementCronOverviewSectionId.TIME_GAP_DISTRIBUTION]: 12,
  [ArrangementCronOverviewSectionId.QUICK_ACTION]: 12,
  [ArrangementCronOverviewSectionId.AR_AGING_OVERVIEW]: 12,
  [ArrangementCronOverviewSectionId.MONTHLY_DISTRIBUTION]: 6,
  [ArrangementCronOverviewSectionId.CREATION_LIFECYCLE]: 6,
  [ArrangementCronOverviewSectionId.CONTRACT_STATUS_CHART]: 12,
  [ArrangementCronOverviewSectionId.METRIC_ANOMALIES]: 12,
  [ArrangementCronOverviewSectionId.ADMIN_DUPLICATES_TABLE]: 12,

  // ── Arrangement Cron — Trends and Recovery (`de_ac_tr_*` + `ac_tr_*` section ids)
  de_ac_tr_stats: 12,
  de_ac_tr_recovery_amount: 12,
  de_ac_tr_critical_insights: 12,
  de_ac_tr_month_recover_performance: 6,
  de_ac_tr_arrangement_type: 6,
  de_ac_tr_success_failure_time: 6,
  de_ac_tr_schedule_recovered_amount: 6,
  de_ac_tr_success_rate_comparison_by_type: 6,
  de_ac_tr_arrangement_categories: 6,
  de_ac_tr_recovery_value_attempt: 6,
  de_ac_tr_recovery_execution_timing: 6,
  de_ac_tr_yoy_performance: 12,
  [ArrangementCronTrendsSectionId.STATS]: 12,
  [ArrangementCronTrendsSectionId.RECOVERY_AMOUNT]: 12,
  [ArrangementCronTrendsSectionId.CRITICAL_INSIGHTS]: 12,
  [ArrangementCronTrendsSectionId.MONTH_RECOVERY_PERFORMANCE]: 6,
  [ArrangementCronTrendsSectionId.ARRANGEMENT_TYPES]: 6,
  [ArrangementCronTrendsSectionId.SUCCESS_FAILURE_TIME]: 6,
  [ArrangementCronTrendsSectionId.SCHEDULE_RECOVERED_AMOUNT]: 6,
  [ArrangementCronTrendsSectionId.SUCCESS_RATE_COMPARISON_BY_TYPE]: 6,
  [ArrangementCronTrendsSectionId.ARRANGEMENT_CATEGORIES]: 6,
  [ArrangementCronTrendsSectionId.RECOVERY_VALUE_ATTEMPT]: 6,
  [ArrangementCronTrendsSectionId.RECOVERY_EXECUTION_TIMING]: 6,
  [ArrangementCronTrendsSectionId.YOY_PERFORMANCE]: 12,

  // ── Arrangement Cron — Admin Performance (`de_ac_ap_*` + `ac_ap_*` section ids)
  de_ac_ap_stats_summary_overview: 12,
  de_ac_ap_top_performer: 4,
  de_ac_ap_duplicate_crisis: 4,
  de_ac_ap_avg_recovery_per_admin: 4,
  de_ac_ap_duplicate_entry_trend_over_time: 6,
  de_ac_ap_duplicate_trend_dod: 6,
  de_ac_ap_performance_distribution_admin_success_rate: 6,
  de_ac_ap_recovery_efficiency: 6,
  de_ac_ap_repeat_arrangement_pattern_analysis: 12,
  de_ac_ap_admin_workload_vs_recovery_output: 12,
  de_ac_ap_admin_productivity_vs_recovery_yield: 12,
  de_ac_ap_duplicate_entries_by_admin: 6,
  de_ac_ap_repeat_arrangement_intensity_dod: 6,
  de_ac_ap_admin_duplicate_exposure: 12,
  de_ac_ap_detailed_duplicate_analysis: 12,
  de_ac_ap_worst_year: 4,
  de_ac_ap_best_year: 4,
  de_ac_ap_latest_year: 4,
  [ArrangementCronAdminPerformanceSectionId.STATS_SUMMARY_OVERVIEW]: 12,
  [ArrangementCronAdminPerformanceSectionId.TOP_PERFORMER]: 4,
  [ArrangementCronAdminPerformanceSectionId.DUPLICATE_CRISIS]: 4,
  [ArrangementCronAdminPerformanceSectionId.AVG_RECOVERY_PER_ADMIN]: 4,
  [ArrangementCronAdminPerformanceSectionId.DUPLICATE_ENTRY_TREND_OVER_TIME]: 6,
  [ArrangementCronAdminPerformanceSectionId.DUPLICATE_TREND_DOD]: 6,
  [ArrangementCronAdminPerformanceSectionId.PERFORMANCE_DISTRIBUTION_SUCCESS]: 6,
  [ArrangementCronAdminPerformanceSectionId.RECOVERY_EFFICIENCY]: 6,
  [ArrangementCronAdminPerformanceSectionId.REPEAT_ARRANGEMENT_PATTERN]: 12,
  [ArrangementCronAdminPerformanceSectionId.ADMIN_WORKLOAD_VS_RECOVERY]: 12,
  [ArrangementCronAdminPerformanceSectionId.ADMIN_PRODUCTIVITY_VS_RECOVERY]: 12,
  [ArrangementCronAdminPerformanceSectionId.DUPLICATE_ENTRIES_BY_ADMIN]: 6,
  [ArrangementCronAdminPerformanceSectionId.REPEAT_ARRANGEMENT_INTENSITY_DOD]: 6,
  [ArrangementCronAdminPerformanceSectionId.ADMIN_DUPLICATE_EXPOSURE]: 12,
  [ArrangementCronAdminPerformanceSectionId.DETAILED_DUPLICATE_ANALYSIS]: 12,
  [ArrangementCronAdminPerformanceSectionId.WORST_YEAR]: 4,
  [ArrangementCronAdminPerformanceSectionId.BEST_YEAR]: 4,
  [ArrangementCronAdminPerformanceSectionId.LATEST_YEAR]: 4,
};

/** Canonical `vb_*` widget keys that belong to the Contracts Report page. */
const CONTRACTS_REPORT_WIDGET_KEYS = new Set<string>([
  'vb_contracts_created_today',
  'vb_contract_status_timeline',
  'vb_state_wise_businesses',
  'vb_top_contracts_platforms',
  'vb_average_contract_value',
  'vb_enrollment_collected_trend',
  'vb_upfront_collected_trend',
  'vb_late_fee_collected_trend',
  'vb_payment_date_fee_trend',
  'vb_downpayment_collected',
  'vb_edit_contract',
  'vb_reversals_trend',
  'vb_refunds',
  'vb_late_fee_refunded',
  'vb_settled_contracts',
  'vb_contract_state_resolution',
  'vb_protected_status_pending',
  'vb_protected_contracts_trend',
  'vb_non_protected_contracts',
]);

/** Backend `widget_key`s registered on the Lendee product-metrics dashboard page. */
const LENDEE_DASHBOARD_WIDGET_KEYS = new Set<string>([
  'le_ln_applications_pipeline',
  'le_ln_offers_responses',
  'le_ln_payment_plans',
  'le_ln_risk_matrix',
  'le_ln_operational_efficiency',
  'le_ln_kpi_metrics',
]);

/** Backend `widget_key`s registered on the Lendee analytical dashboard page. */
const LENDEE_ANALYTICS_WIDGET_KEYS = new Set<string>([
  'le_ln_operational_kpis',
  'le_ln_key_insights',
  'le_ln_recipient_conversion_funnel',
  'le_ln_request_offer_performance',
  'le_ln_signups_trend',
  'le_ln_request_status_summary',
  'le_ln_offer_outcome_analysis',
  'le_ln_overdue_collections_progress',
  'le_ln_partial_repayments_trend',
  'le_ln_receivables_completion_revenue',
  'le_ln_receivables_recovery',
  'le_ln_top_decline_reasons',
]);

/** Backend `widget_key`s registered on the Cool Credit analytical dashboard page. */
const COOL_CREDIT_DASHBOARD_WIDGET_KEYS = new Set<string>([
  'co_cc_business_growth',
  'co_cc_activation_calls',
  'co_cc_product_usage',
  'co_cc_monetization_revenue',
  'co_cc_plan_lifecycle',
  'co_cc_disputes',
]);

/** Backend `widget_key`s registered on the Cool Credit growth dashboard page. */
const COOL_CREDIT_GROWTH_WIDGET_KEYS = new Set<string>([
  'co_ga_growth_revenue_counters',
  'co_ga_growth_key_insights',
  'co_ga_journey_dropoff_comparison',
  'co_ga_onboarding_funnel',
  'co_ga_revenue_by_plan',
  'co_ga_cbc_fetch_outcomes',
  'co_ga_payment_method_distribution',
  'co_ga_simulator_usage_distribution',
  'co_ga_disputes_calls_trend',
  'co_ga_call_source_distribution',
  'co_ga_plan_lifecycle_status',
  'co_ga_page_session_distribution',
]);

/** Canonical `dc_*` widget keys registered on the Daily Cron Payments page. */
const DAILY_CRON_WIDGET_KEYS = new Set<string>(Object.values(DailyCronSectionId));

/** Canonical `oc_*` widget keys registered on the Overdue Cron Payments page. */
const OVERDUE_CRON_WIDGET_KEYS = new Set<string>(Object.values(OverdueCronSectionId));

/** Backend + canonical widget keys on the Arrangement Cron Overview Table page. */
const ARRANGEMENT_CRON_OVERVIEW_WIDGET_KEYS = new Set<string>([
  ...Object.values(ArrangementCronOverviewSectionId),
  ...Object.values(ARRANGEMENT_CRON_OVERVIEW_BACKEND_WIDGET_KEYS),
]);

/** Backend + canonical widget keys on the Arrangement Cron Trends page. */
const ARRANGEMENT_CRON_TRENDS_WIDGET_KEYS = new Set<string>([
  ...Object.values(ArrangementCronTrendsSectionId),
  ...Object.values(ARRANGEMENT_CRON_TRENDS_BACKEND_WIDGET_KEYS),
]);

/** Backend + canonical widget keys on the Arrangement Cron Admin Performance page. */
const ARRANGEMENT_CRON_ADMIN_PERFORMANCE_WIDGET_KEYS = new Set<string>([
  ...Object.values(ArrangementCronAdminPerformanceSectionId),
  ...Object.values(ARRANGEMENT_CRON_ADMIN_PERFORMANCE_BACKEND_WIDGET_KEYS),
]);

/** Slug used by the unified grid `@switch` to pick the embedded report component. */
export type EmbeddedReportKey =
  | 'monthly-report-1'
  | 'monthly-report-2'
  | 'social-platform'
  | 'seo-stats'
  | 'seo-overview'
  | 'campaign-ads-stats'
  | 'industry-categorization'
  | 'training-dashboard'
  | 'business-performance-report'
  | 'contracts-report'
  | 'practina-dashboard'
  | 'lendee-dashboard'
  | 'lendee-analytics-dashboard'
  | 'cool-credit-dashboard'
  | 'growth-activation-and-revenue'
  | 'daily-cron-payments'
  | 'overdue-cron-payments'
  | 'arrangement-cron-overview'
  | 'arrangement-cron-trends'
  | 'arrangement-cron-admin-performance';

/**
 * Backend `widget_key`s that must NEVER surface in the My Dashboard view —
 * the unified grid, the customize-state preview, or the visible-widget count.
 * Audit Log sections still exist on their parent report pages (Social Platform,
 * SEO Stats, Campaign Ads Stats), but they are explicitly excluded from the
 * pinned/customized dashboard experience.
 */
const EXCLUDED_WIDGET_KEYS: ReadonlySet<string> = new Set([
  SocialPlatformSectionId.AUDIT_LOG,
  SeoStatsSectionId.AUDIT_LOG,
  CampaignAdsStatsSectionId.AUDIT_LOG,
]);

@Component({
  selector: 'app-my-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    IconComponent,
    FormsModule,
    DragDropModule,
    DatePickerComponent,
    // CustomizeDashboardStateComponent,
    // CustomizeDashboardStateComponent,
    WidgetHostComponent,
    IndividualToasterComponent,
  ],
  templateUrl: './my-dashboard.component.html',
  host: {
    class: 'block  bg-white/96 dark:bg-surface-0 text-slate-900 dark:text-slate-200 overflow-clip rounded-xl'
  },
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [`  
        /* Collapsing & Sliding Header */

        .dashboard-header {
          display: grid;
          grid-template-columns: minmax(0, 1fr);
          grid-template-areas:
            'title'
            'controls';
          column-gap: 1.5rem;
          row-gap: 1rem;
          padding-bottom: 1rem;
          transition:
            padding-top 300ms ease,
            padding-bottom 300ms ease,
            row-gap 300ms ease;
        }

        @media (min-width: 1024px) {
          .dashboard-header {
            grid-template-columns: minmax(0, 1fr) auto;
            grid-template-areas: 'title controls';
            row-gap: 0;
          }
        }

        .dashboard-header__title {
          grid-area: title;
          min-width: 0;
        }
        .dashboard-header__controls {
          grid-area: controls;
        }

        .dashboard-header__title-text {
          font-size: 1.5rem; /* text-2xl */
          line-height: 2rem;
          transition: font-size 300ms ease, line-height 300ms ease;
        }

        .dashboard-header__desc {
          display: grid;
          grid-template-rows: 1fr;
          opacity: 1;
          margin-top: 0.25rem;
          transition:
            grid-template-rows 300ms ease,
            opacity 250ms ease,
            margin-top 300ms ease;
        }
        .dashboard-header__desc > span {
          overflow: hidden;
          min-height: 0;
        }

        /* ── Collapsed state ─────────────────────────────────────────────── */
        .dashboard-header.is-collapsed {
          grid-template-columns: minmax(0, 1fr) auto;
          grid-template-areas: 'title controls';
          padding-top: 0.75rem;
          padding-bottom: 0.75rem;
          row-gap: 0;
        }
        .dashboard-header.is-collapsed .dashboard-header__title-text {
          font-size: 1.125rem; /* text-lg */
          line-height: 1.5rem;
        }
        .dashboard-header.is-collapsed .dashboard-header__desc {
          grid-template-rows: 0fr;
          opacity: 0;
          margin-top: 0;
        }

        /* Respect reduced-motion users — drop the transitions but keep the
         * end-state styles so the layout still collapses, just instantly. */
        @media (prefers-reduced-motion: reduce) {
          .dashboard-header,
          .dashboard-header__title-text,
          .dashboard-header__desc {
            transition: none;
          }
        }

        /* 1. Only strip deep card-bg layouts for MR1 and MR2 to avoid visual duplication */
        :host ::ng-deep .unified-tile > app-widget-host > app-monthly-report-1 .card-bg,
        :host ::ng-deep .unified-tile > app-widget-host > app-monthly-report-2 .card-bg {
          background: transparent !important;
          border: none !important;
          box-shadow: none !important;
          padding: 0 !important;
        } 
          
        :host ::ng-deep .unified-tile > app-widget-host > app-campaign-ads-stats .card-bg {
          background: transparent !important;
          border: none !important;
          box-shadow: none !important;
          padding: calc(var(--spacing) * 2);
        }
          
        /* Strip duplicate outer .card-bg shell on components that wrap their
           entire content in a .card-bg at the page root level. app-daily-cron
           and app-overdue-cron are intentionally excluded here: they use
           .unified-tile-body for their outer shell (stripped separately below)
           and their inner chart/section cards also use .card-bg — those inner
           card borders must be preserved. */
        :host ::ng-deep .unified-tile > app-widget-host > :is(app-social-platform,app-seo-stats,app-seo-overview,app-business-performance-report,app-contracts-report) .card-bg {
          background: transparent !important;
          background-image: none !important;
          border: none !important;
          box-shadow: none !important;
        }

        /* Strip duplicate outer .card-bg shell on app-training-dashboard sections.
           We target the direct child .card-bg under .group.relative so that inner
           nested .card-bg elements (like the individual stats overview cards)
           preserve their borders and backgrounds. */
        :host ::ng-deep .unified-tile > app-widget-host > app-training-dashboard .group.relative > .card-bg {
          background: transparent !important;
          background-image: none !important;
          border: none !important;
          box-shadow: none !important;
        }

        /* ── Cron components: strip the inner .unified-tile-body card-chrome
           (border + padding + background + shadow) when embedded inside a
           .unified-tile. The outer .unified-tile wrapper already provides the
           visible card shell — keeping the inner shell creates a double-border
           and double-padding effect drawn outside the card content area.
           border-radius is also reset so it doesn't clip inner content against
           the outer tile's rounded corners. */
        :host ::ng-deep .unified-tile > app-widget-host > :is(
            app-daily-cron,
            app-overdue-cron,
            app-overview-new,
            app-trends,
            app-admin-performance
          ) > .unified-tile-body {
          border: none !important;
          padding: 0 !important;
          box-shadow: none !important;
          background-color: transparent !important;
          border-radius: 0 !important;
        }

        /* ── Cron section cards: add card border + background to every
           direct content div inside a section wrapper for app-daily-cron
           and app-overdue-cron. Some sections (funnel charts, timeline)
           render a bare div with no border class of their own. This rule
           gives them the same card chrome as chart sections that use
           [cardClass]="'card-bg'", so every pinned cron card has a
           consistent visible border in the dashboard.
           The :not(.card-bg) guard prevents double-applying to sections
           whose outer div already carries the card-bg class. */
        :host ::ng-deep .unified-tile > app-widget-host > :is(app-daily-cron, app-overdue-cron)
          .group.relative > div:first-of-type:not(.card-bg):not([class*="absolute"]) {
          border: 1px solid rgba(234, 234, 234, 1);
          border-radius: 0.75rem;
          background-color: #ffffff;
          overflow: visible;
          height: 100%;
        }

        :host-context(.dark) ::ng-deep .unified-tile > app-widget-host > :is(app-daily-cron, app-overdue-cron)
          .group.relative > div:first-of-type:not(.card-bg):not([class*="absolute"]) {
          border-color: rgba(58, 58, 120, 1);
          background-color: rgba(21, 21, 23, 1);
        }

        /* Contracts Report — most of its sub-widgets (Contract Status,
           State Contracts, Contract Count Stats, Payment Collection
           Cards) do NOT use the shared .card-bg helper. They paint
           their own white/dark background, border, shadow, and
           backdrop-blur via raw Tailwind utility classes on the
           outermost wrapper div instead, e.g.:
              bg-white border border-white/20 dark:border-line
              dark:bg-surface-1 rounded-lg shadow-md backdrop-blur-3xl
           Inside a .unified-tile the outer tile chrome already
           provides those, so the inner chrome renders as a visible
           "double outer border" (see Contract Status / Active
           Contracts table card). Strip the inner chrome on the actual
           card wrapper of each contracts sub-widget so the unified
           tile is the only card outline. Padding is preserved so the
           inner content keeps its breathing room. */
        :host ::ng-deep .unified-tile > app-widget-host > app-contracts-report app-contract-status > div,
        :host ::ng-deep .unified-tile > app-widget-host > app-contracts-report app-state-contracts > div,
        :host ::ng-deep .unified-tile > app-widget-host > app-contracts-report app-contract-count-stats > div > div,
        :host ::ng-deep .unified-tile > app-widget-host > app-contracts-report app-payment-collection-cards > div > div {
          background: transparent !important;
          border: none !important;
          box-shadow: none !important;
          backdrop-filter: none !important;
          -webkit-backdrop-filter: none !important;
        }

        /* businesses-registered-stats and contract-count-stats toggle a
           .blur-sm filter on their right-hand dark stats panel
           ("Business Registered Today" / "Contracts Registered Today")
           while the today_business / today_contracts API is in flight.
           Inside the unified dashboard tile that loading flag can stay
           latched (the request resolves outside the OnPush widget-host
           CD cycle), leaving the dark panel permanently blurred and
           unreadable. Those cards already render a dedicated dark
           loader overlay (app-individual-toaster with loaderType dark),
           so suppress the redundant content-level blur whenever the
           card is hosted inside a unified tile. */
        :host ::ng-deep .unified-tile .blur-sm {
          filter: none !important;
        }

        /* Social Platform standalone sub-widgets (e.g. "Watch Time Contribution (Top 3)",
           "Followers by Platform", etc.) wrap their content with a .card-bg
           that applies its own background/border/shadow. Inside .unified-tile-body
           those styles produce a doubled inner border + shadow on top of the
           outer unified tile chrome, so flatten the inner .card-bg here. */
        :host ::ng-deep .unified-tile > app-widget-host > app-social-platform .card-bg {
          background: transparent !important;
          border: none !important;
          box-shadow: none !important;
          // padding: 0 ! important;
        }

        /* SEO Stats embedded sections (e.g. "AI Overview Keyword", "Performance in
           Top 10 Countries", "Top Keyword Ranking", etc.) each wrap their content
           in a .card-bg rounded-2xl tile. The outer .unified-tile already provides
           the border, background, and shadow, so the inner .card-bg renders as a
           duplicate outer border. Flatten it the same way as the other embedded
           dashboards above. */
        :host ::ng-deep .unified-tile > app-widget-host > app-seo-stats .card-bg {
          background: transparent !important;
          border: none !important;
          box-shadow: none !important;
        }
        /* 2. Flatten grid behavior within individual tiles to prevent responsive layout snapping */
        :host ::ng-deep .unified-tile .grid.grid-cols-12 {
          display: block !important;
          margin-top: 0 !important;
          gap: 0 !important;
        }
        :host ::ng-deep .unified-tile .grid.grid-cols-12 > * {
          grid-column: auto !important;
          width: 100% !important;
        }
    
        :host ::ng-deep .unified-tile > app-widget-host > app-monthly-report-1,
        :host ::ng-deep .unified-tile > app-widget-host > app-monthly-report-2,
        :host ::ng-deep .unified-tile > app-widget-host > app-social-platform,
        :host ::ng-deep .unified-tile > app-widget-host > app-seo-stats,
        :host ::ng-deep .unified-tile > app-widget-host > app-seo-overview,
        :host ::ng-deep .unified-tile > app-widget-host > app-campaign-ads-stats,
        :host ::ng-deep .unified-tile > app-widget-host > app-industry-wise-categorization,
        :host ::ng-deep .unified-tile > app-widget-host > app-training-dashboard,
        :host ::ng-deep .unified-tile > app-widget-host > app-business-performance-report,
        :host ::ng-deep .unified-tile > app-widget-host > app-contracts-report,
        :host ::ng-deep .unified-tile > app-widget-host > app-practina-dashboard,
        :host ::ng-deep .unified-tile > app-widget-host > app-lendee-dashboard,
        :host ::ng-deep .unified-tile > app-widget-host > app-lendee-analytics-dashboard,
        :host ::ng-deep .unified-tile > app-widget-host > app-cool-credit-dashboard,
        :host ::ng-deep .unified-tile > app-widget-host > app-daily-cron,
        :host ::ng-deep .unified-tile > app-widget-host > app-overdue-cron,
        :host ::ng-deep .unified-tile > app-widget-host > app-overview-new,
        :host ::ng-deep .unified-tile > app-widget-host > app-trends,
        :host ::ng-deep .unified-tile > app-widget-host > app-admin-performance {
          display: block;
          height: 100%;
          min-height: 0;
        }

        :host ::ng-deep .unified-tile > app-widget-host > app-growth-activation-and-revenue {
          display: block;
          height: auto;
          min-height: 0;
        }

        /* Specific height and layout rules for Growth, Activation & Revenue cards on My Dashboard */
        :host ::ng-deep .unified-tile:is(
            .co_ga_cbc_fetch_outcomes, .cc_gar_cbc_effect,
            .co_ga_payment_method_distribution, .cc_gar_payment_method,
            .co_ga_simulator_usage_distribution, .cc_gar_simulator_usage
          ) > app-widget-host > app-growth-activation-and-revenue {
          height: 100%;
        }
        :host ::ng-deep .unified-tile:is(
            .co_ga_cbc_fetch_outcomes, .cc_gar_cbc_effect,
            .co_ga_payment_method_distribution, .cc_gar_payment_method,
            .co_ga_simulator_usage_distribution, .cc_gar_simulator_usage
          ) > app-widget-host > app-growth-activation-and-revenue > div,
        :host ::ng-deep .unified-tile:is(
            .co_ga_cbc_fetch_outcomes, .cc_gar_cbc_effect,
            .co_ga_payment_method_distribution, .cc_gar_payment_method,
            .co_ga_simulator_usage_distribution, .cc_gar_simulator_usage
          ) > app-widget-host > app-growth-activation-and-revenue > div > div,
        :host ::ng-deep .unified-tile:is(
            .co_ga_cbc_fetch_outcomes, .cc_gar_cbc_effect,
            .co_ga_payment_method_distribution, .cc_gar_payment_method,
            .co_ga_simulator_usage_distribution, .cc_gar_simulator_usage
          ) > app-widget-host > app-growth-activation-and-revenue > div > div > div {
          height: 100%;
        }
        :host ::ng-deep .unified-tile:is(
            .co_ga_cbc_fetch_outcomes, .cc_gar_cbc_effect,
            .co_ga_payment_method_distribution, .cc_gar_payment_method,
            .co_ga_simulator_usage_distribution, .cc_gar_simulator_usage
          ) .grid.grid-cols-1 {
          height: 100%;
        }
        :host ::ng-deep .unified-tile:is(
            .co_ga_payment_method_distribution, .cc_gar_payment_method,
            .co_ga_simulator_usage_distribution, .cc_gar_simulator_usage
          ) .grid.grid-cols-1 > div {
          height: 100%;
        }

        :host ::ng-deep .unified-tile > app-widget-host > app-cool-credit-dashboard > .grid {
          height: 100%;
        }

        :host ::ng-deep .unified-tile > app-widget-host > app-cool-credit-dashboard .card-bg {
          height: 100%;
        }

        :host ::ng-deep .unified-tile > app-widget-host > app-practina-dashboard {
          min-height: 0;
        }

        /* Practina: strip standalone page chrome — unified-tile-body is the only card border */
        :host ::ng-deep .unified-tile > app-widget-host > app-practina-dashboard > div {
          border: none !important;
          padding: 0 !important;
          background: transparent !important;
          min-height: 0 !important;
          box-shadow: none !important;
        }

        :host ::ng-deep .unified-tile > app-widget-host > app-practina-dashboard .unified-tile-body {
          min-height: 0 !important;
          height: 100%;
        }

        :host ::ng-deep .unified-tile > app-widget-host > app-practina-dashboard .unified-tile-body > .grid.grid-cols-12 {
          display: flex !important;
          flex-direction: column;
          height: 100%;
        }
        
        :host ::ng-deep .unified-tile > app-widget-host > app-practina-dashboard .unified-tile-body > .grid.grid-cols-12 > * {
          height: 100%;
          display: flex;
          flex-direction: column;
          min-height: 0;
        }

        :host ::ng-deep .unified-tile > app-widget-host > app-practina-dashboard .unified-tile-body > .grid.grid-cols-12 > * > div:not(.absolute) {
          flex: 1 1 auto;
          height: 100%;
        }

        :host ::ng-deep .unified-tile > app-widget-host > app-lendee-dashboard,
        :host ::ng-deep .unified-tile > app-widget-host > app-lendee-analytics-dashboard {
          min-height: 0;
        }

        :host ::ng-deep .unified-tile > app-widget-host > app-lendee-dashboard .unified-tile-body,
        :host ::ng-deep .unified-tile > app-widget-host > app-lendee-analytics-dashboard .unified-tile-body {
          min-height: 0 !important;
          height: 100%;
        }

        /* 3. Re-instantiate standard card container chrome layout directly on the custom unified tile body wrappers */
        :host ::ng-deep .unified-tile-body {
          height: 100%;
          display: flex;
          flex-direction: column;
          background-color: rgb(255 255 255); /* Matches light theme fallback */
          border: 1px solid rgba(234, 234, 234, 1);
          border-radius: 0.75rem; /* rounded-xl */
          box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1); /* shadow-sm */
          padding: 1.5rem; /* p-6 */
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        /* Lift the embedded card body shadow when the outer unified-tile is hovered
           (same feel as monthly-report-1 / social-platform card hover). */
        :host ::ng-deep .unified-tile:hover .unified-tile-body {
          box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1); /* shadow-md */
          border-color: rgba(42, 42, 46, 0.4); 
        }

        /* Dark mode styling overrides for the synthesized widget wrapper container */
        :host-context(.dark) ::ng-deep .unified-tile-body {
          background-color: rgba(21, 21, 23, 1);
          border-color: rgb(5, 5, 24);
          box-shadow: 0 25px 50px -12px rgb(0 0 0 / 0.25); /* dark:shadow-2xl */
        }

        :host-context(.dark) ::ng-deep .unified-tile:hover .unified-tile-body {
          box-shadow: 0 10px 30px -10px rgba(22, 22, 32, 0.35);
          border-color: rgba(4, 4, 17, 0.6); 
        }
        
        :host ::ng-deep .unified-tile-body > .grid.grid-cols-12,
        :host ::ng-deep .unified-tile-body > .min-w-0 {
          flex: 1 1 auto;
          min-height: 0;
          display: flex;
          flex-direction: column;
        }
        :host ::ng-deep .unified-tile-body > .min-w-0 > .grid.grid-cols-12 {
          flex: 1 1 auto;
          min-height: 0;
        }
        :host ::ng-deep .unified-tile-body > .min-w-0 > .grid.grid-cols-12:not(:has(> div)) {
          display: none !important;
        }
        :host ::ng-deep .unified-tile-body > .grid.grid-cols-12 > *,
        :host ::ng-deep .unified-tile-body > .min-w-0 > .grid.grid-cols-12 > * {
          height: 100%;
        }

        /* ─────── Equalize card heights row-wise ───────
           The outer grid already stretches every .unified-tile in a row
           to the tallest tile (items-stretch + h-full). These rules
           cascade height:100% through each embedded report's inner
           wrapper chain (root div and .card-bg) so the visible
           card fills the tile, making every card in the same row the
           same visual height. */
        :host ::ng-deep .unified-tile > app-widget-host > * > div {
          height: 100%;
        }
        :host ::ng-deep .unified-tile > app-widget-host > * > .card-bg,
        :host ::ng-deep .unified-tile > app-widget-host > * > div > .card-bg,
        :host ::ng-deep .unified-tile > app-widget-host > * > div > div > .card-bg {
          height: 100%;
        }
        :host ::ng-deep .unified-tile .card-bg > .unified-tile-body {
          height: 100%;
        }

     

        /* Training Status tile: chain height: 100% through the embedded MR2 DOM so the
           single "Training Status" card stretches to match the height of its row
           neighbour (e.g. the much taller "MoM Active Businesses" chart). */
        :host ::ng-deep .unified-tile.training-status-fit > app-widget-host > app-monthly-report-2 > div {
          height: 100%;
        }
        :host ::ng-deep .unified-tile.training-status-fit > app-widget-host > app-monthly-report-2 > div > .card-bg {
          height: 100%;
        }
        :host ::ng-deep .unified-tile.training-status-fit .card-bg > .grid.grid-cols-12 {
          display: flex !important;
          flex-direction: column;
          height: 100%;
        }
        :host ::ng-deep .unified-tile.training-status-fit .card-bg > .grid.grid-cols-12 > * {
          height: 100%;
          display: flex;
          flex-direction: column;
          min-height: 0;
        }
        :host ::ng-deep .unified-tile.training-status-fit .card-bg > .grid.grid-cols-12 > * > .p-4 {
          flex: 1 1 auto;
          display: flex;
          flex-direction: column;
          min-height: 0;
        }
        :host ::ng-deep .unified-tile.training-status-fit .card-bg > .grid.grid-cols-12 > * > .p-4 > .overflow-y-auto {
          flex: 1 1 auto;
          min-height: 0;
        }

        /* Visual polish configurations while a tile is being actively dragged across lists */
        :host ::ng-deep .unified-tile.cdk-drag-preview {
          box-shadow: 0 18px 40px rgba(15, 23, 42, 0.25);
          border-radius: 1rem;
        }
        :host ::ng-deep .unified-tile.cdk-drag-placeholder {
          opacity: 0;
        }
        :host ::ng-deep .cdk-drop-list-dragging .unified-tile:not(.cdk-drag-placeholder) {
          transition: transform 200ms cubic-bezier(0, 0, 0.2, 1);
        }

        /* Prevent duplicate borders, shadows, and backgrounds for black cron cards since they carry their own outer border and background layout styles */
        :host ::ng-deep .unified-tile.is-black-card {
          border: none !important;
          box-shadow: none !important;
          background: transparent !important;
          overflow: visible !important;
        }

        :host ::ng-deep .unified-tile.is-black-card:hover {
          box-shadow: none !important;
        }

        /* Prevent infinite height growth for small year summary cards */
        :host ::ng-deep .unified-tile:is(.ac_ap_worst_year, .ac_ap_best_year, .ac_ap_latest_year, .de_ac_ap_worst_year, .de_ac_ap_best_year, .de_ac_ap_latest_year) {
          height: auto !important;
        }
        :host ::ng-deep .unified-tile:is(.ac_ap_worst_year, .ac_ap_best_year, .ac_ap_latest_year, .de_ac_ap_worst_year, .de_ac_ap_best_year, .de_ac_ap_latest_year) > app-widget-host > app-admin-performance {
          height: auto !important;
        }

        /* ── Restore Slide Panel card-bg styles in My Dashboard (both light & dark themes, teleported or not) ── */
        ::ng-deep app-slide-panel .card-bg,
        ::ng-deep app-slide-panel div.card-bg,
        body app-slide-panel .card-bg,
        body app-slide-panel div.card-bg,
        :host ::ng-deep .unified-tile app-slide-panel .card-bg,
        :host ::ng-deep .unified-tile > app-widget-host > * app-slide-panel .card-bg,
        :host ::ng-deep .unified-tile > app-widget-host > :is(app-social-platform,app-seo-stats,app-seo-overview,app-business-performance-report,app-contracts-report) app-slide-panel .card-bg,
        :host ::ng-deep .unified-tile > app-widget-host > app-social-platform app-slide-panel .card-bg,
        :host ::ng-deep app-slide-panel .card-bg {
          background-color: #ffffff !important;
          background-image: none !important;
          border: 1px solid rgba(234, 234, 234, 1) !important;
          box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1) !important;
          height: 100% !important;
          display: flex !important;
          flex-direction: column !important;
          overflow: hidden !important;
        }

        .dark ::ng-deep app-slide-panel .card-bg,
        .dark ::ng-deep app-slide-panel div.card-bg,
        html.dark ::ng-deep app-slide-panel .card-bg,
        html.dark ::ng-deep app-slide-panel div.card-bg,
        html.dark body app-slide-panel .card-bg,
        :host-context(.dark) ::ng-deep .unified-tile app-slide-panel .card-bg,
        :host-context(.dark) ::ng-deep .unified-tile > app-widget-host > * app-slide-panel .card-bg,
        :host-context(.dark) ::ng-deep .unified-tile > app-widget-host > :is(app-social-platform,app-seo-stats,app-seo-overview,app-business-performance-report,app-contracts-report) app-slide-panel .card-bg,
        :host-context(.dark) ::ng-deep .unified-tile > app-widget-host > app-social-platform app-slide-panel .card-bg,
        :host-context(.dark) ::ng-deep app-slide-panel .card-bg {
          background-color: rgba(21, 21, 23, 1) !important;
          border: 1px solid rgba(42, 42, 46, 1) !important;
        }
      `]
})
export class MyDashboardComponent implements OnInit, AfterViewInit, OnDestroy {
  private router = inject(Router);
  private layoutService = inject(DashboardLayoutService);
  private dialog = inject(MatDialog);
  private toaster = inject(ToasterService);
  private apiService = inject(ApiService);

  /**
   * Sentinel placed immediately before the sticky header. While it is visible
   * in the scrollport, the page is "at the top" and the header renders in its
   * full expanded form. Once the user scrolls past the sentinel (sticky kicks
   * in) the IntersectionObserver flips `headerCollapsed` to true and the
   * header transitions into the compact single-line layout.
   */
  @ViewChild('headerSentinel', { static: false }) private headerSentinel?: ElementRef<HTMLElement>;
  private headerObserver?: IntersectionObserver;
  protected headerCollapsed = signal(false);

  searchQuery = signal('');
  currentView = signal<'reports' | 'management'>('reports');
  layoutLoading = signal(false);
  //  mixed mode on/off state
  mixedEnabled = signal(true);
  //  date range picker signal
  currentDateRange = signal<DateRangePickerOutput | null>({
    start_date: dayjs().tz(LA_TIMEZONE).subtract(29, 'day').startOf('day').utc().toISOString(),
    end_date: dayjs().tz(LA_TIMEZONE).endOf('day').utc().toISOString(),
    previous_start_date: null,
    previous_end_date: null,
    unit: 'day',
    comparison_type: 'none',
    preset_label: 'Last 30 Days',
  });

  /**
   * Adapter signal used to feed the saved range back into `<app-date-picker [value]>`.
   * Without this, if the picker is destroyed/recreated (e.g. user removes the last
   * tile and re-adds one), `ngOnInit` falls back to its hard-coded "Last 7 Days"
   * default and silently overwrites whatever range the user had previously chosen
   * (e.g. "Last 90 Days"). Feeding the stored range back as `[value]` keeps the
   * picker in sync with the user's selection across remounts.
   */
  currentDatePickerValue = computed<DateRangePickerValue | undefined>(() => {
    const range = this.currentDateRange();
    if (!range) return undefined;
    return {
      startDate: range.start_date,
      endDate: range.end_date,
      unit: range.unit,
      comparisonType: range.comparison_type,
      comparisonStartDate: range.previous_start_date ?? undefined,
      comparisonEndDate: range.previous_end_date ?? undefined,
      presetLabel: range.preset_label,
    };
  });

  // Intentionally not readonly — reset on every catalog reload so freshly
  // mounted widget instances always receive the current date-range on init.
  private hydratedReportRefs = new WeakSet<object>();
  private dashboardDateEmitCount = 1;
  private lastLoadedProductId: string | number | null = null;
  /**
   * Tracks whether the initial `loadCatalogData()` has already fired
   * (whether from `ngOnInit` synchronously when `selectedProductId` is
   * already set, or from the constructor effect once the sidebar
   * resolves the product). Replaces the old `skipNextProductEffect`
   * flag whose semantics were "always skip the first emission and let
   * ngOnInit drive the initial load" — that pattern caused a double
   * `/dashboards` call whenever the sidebar resolved the product after
   * the component mounted.
   */
  private hasFiredInitialLoad = false;
  private loadCatalogVersion = 0;

  constructor() {
    effect(() => {
      const selectedProductId = this.apiService.selectedProductId();

      // Initial-load gate. If we haven't fired the catalog load yet and
      // the sidebar hasn't resolved a product id, do nothing — wait for
      // the next emission. This avoids the previous double-fire where
      // `ngOnInit` would call `loadCatalogData()` with a null
      // `selectedProductId`, fall back to the first numeric product in
      // `/catalog/products` (e.g. id=3), fire `/dashboards?product_id=3`,
      // and then immediately re-fire once the sidebar resolved the
      // actual selection (e.g. Denefits = id=1).
      if (!this.hasFiredInitialLoad && selectedProductId == null) {
        return;
      }

      if (this.hasFiredInitialLoad && this.lastLoadedProductId === selectedProductId) {
        return;
      }

      this.hasFiredInitialLoad = true;
      this.lastLoadedProductId = selectedProductId;
      untracked(() => this.loadCatalogData());
    });

    effect(() => {
      // Track the signal so we re-run when the map identity changes.
      this.reportRefs();
      untracked(() => this.hydrateReportRefs());
    });
  }

  /** True when the dashboards API call for page_id=1 returns layout data. Controls visibility of the "Edit Your Settings" button. */
  hasDashboardData = signal(false);

  /** Snapshot of widget visibility as loaded from the API. Key: "dashboardId|widgetId". */
  private initialVisibilitySnapshot = signal<Map<string, boolean>>(new Map());

  /** True when any dashboard is new (no layout yet) OR any widget visibility differs from the snapshot. */
  hasPendingChanges = computed(() => {
    const snapshot = this.initialVisibilitySnapshot();
    for (const db of this.dashboards()) {
      if (db.initialWidgetsEmpty || !db.dashboardApiId) return true;
      for (const w of db.widgets) {
        const key = `${db.id}|${w.id}`;
        if (snapshot.get(key) !== w.visible) return true;
      }
    }
    return false;
  });

  availableReports = signal<ReportPage[]>([
    {
      id: 'rp-1',
      pageId: 1,
      title: 'Monthly Report 1',
      icon: 'chartBar',
      route: '/dashboard/monthly-report-1',
      color: 'bg-blue-500',
      reportKey: 'monthly-report-1',
    },
    {
      id: 'rp-2',
      pageId: 2,
      title: 'Monthly Report 2',
      icon: 'chartPie',
      route: '/dashboard/monthly-report-2',
      color: 'bg-purple-500',
      reportKey: 'monthly-report-2',
    },
    {
      id: 'rp-social',
      title: 'Social Media Performance',
      icon: 'smartphone',
      route: '/dashboard/social-platform',
      color: 'bg-pink-500',
      reportKey: 'social-platform',
    },
    {
      id: 'rp-seo-stats',
      title: 'SEO Stats',
      icon: 'trendingUp',
      route: '/dashboard/seo-stats',
      color: 'bg-emerald-500',
      reportKey: 'seo-stats',
    },
    {
      id: 'rp-seo-overview',
      title: 'Ads Overview',
      icon: 'target',
      route: '/dashboard/seo-overview',
      color: 'bg-cyan-500',
      reportKey: 'seo-overview',
    },
    {
      id: 'rp-campaign',
      title: 'Campaign Ads Stats',
      icon: 'target',
      route: '/dashboard/campaign-ads-stats',
      color: 'bg-amber-500',
      reportKey: 'campaign-ads-stats',
    },
    {
      id: 'rp-industry',
      title: 'Industry Categorization',
      icon: 'building2',
      route: '/dashboard/industry-categorization',
      color: 'bg-indigo-500',
      reportKey: 'industry-categorization',
    },
    {
      id: 'rp-training',
      title: 'Training and Demo',
      icon: 'users',
      route: '/dashboard/training',
      color: 'bg-orange-500',
      reportKey: 'training-dashboard',
    },
  ]);

  dashboards = signal<Dashboard[]>([]);

  private reportRefs = signal<
    ReadonlyMap<string, { instance: object; reportKey: EmbeddedReportKey }>
  >(new Map());

  onWidgetInstanceReady(
    tile: VisibleWidgetTile,
    event: WidgetHostInstanceEvent,
  ): void {
    this.reportRefs.update((prev) => {
      const next = new Map(prev);
      if (event.instance && event.reportKey) {
        next.set(tile.uniqueKey, {
          instance: event.instance,
          reportKey: event.reportKey,
        });
      } else {
        next.delete(tile.uniqueKey);
      }
      return next;
    });

    if (event.instance) {
      // Defer hydration to the next microtask so the component is fully
      // initialized before receiving the date range dispatch.
      Promise.resolve().then(() => this.hydrateReportRefs());
    }
  }

  /** Re-fetches each embedded report's personalized layout so any persisted width / visibility / order changes are reflected on screen immediately. */
  private reloadEmbeddedReports(): void {
    for (const { instance, reportKey } of this.reportRefs().values()) {
      if (reportKey !== 'monthly-report-1' && reportKey !== 'monthly-report-2') continue;
      const fn = (instance as { loadPersonalizedDashboardLayout?: () => void })
        .loadPersonalizedDashboardLayout;
      if (typeof fn === 'function') fn.call(instance);
    }
  }

  private forEachReportRef(
    fn: (ref: object, reportKey: EmbeddedReportKey) => void,
  ): void {
    for (const { instance, reportKey } of this.reportRefs().values()) {
      fn(instance, reportKey);
    }
  }

  private dispatchDateRangeToRef(
    ref: object,
    payload: DateRangePickerOutput,
  ): void {
    const anyRef = ref as Record<string, unknown>;
    try {
      if (typeof anyRef['onDateRangeChange'] === 'function') {
        (anyRef['onDateRangeChange'] as (e: DateRangePickerOutput) => void)(payload);
        return;
      }
      if (typeof anyRef['onDateRangeFilter'] === 'function') {
        (anyRef['onDateRangeFilter'] as (e: DateRangePickerOutput) => void)(payload);
        return;
      }
    } catch (err) {
      console.error(err, ref);
    }
  }

  private static readonly MONTH_UNIT_REPORT_KEYS: ReadonlySet<EmbeddedReportKey> = new Set([
    'social-platform',
    'seo-stats',
    'campaign-ads-stats',
    'practina-dashboard',
  ]);

  /**
   * Units the month-cadence reports above are actually allowed to send to
   * their backend. The seo-stats `click-performance-details` endpoint (and
   * its siblings on Social / Campaign Ads) only understands
   * `month` and `quarter` — passing `day` / `week` / `year` produces a 4xx
   * because those granularities have no aggregation path on the server.
   *
   * My Dashboard's date picker, on the other hand, can emit any of
   * `day | week | month | quarter | year` depending on what the user
   * selected ("Last 7 Days" → `day`, "Last 2 Years" → `year`, etc.). When
   * one of those reports is embedded on My Dashboard the unit flowed
   * through as-is and broke the embedded card. Coerce anything outside
   * this allow-list back to `month` while still preserving an explicit
   * `quarter` pick from the user.
   */
  private static readonly MONTH_UNIT_ALLOWED_UNITS: ReadonlySet<string> = new Set([
    'month',
    'quarter',
    'year',
  ]);

  private static readonly SELF_SEEDED_DATE_RANGE_KEYS: ReadonlySet<EmbeddedReportKey> = new Set([
    'social-platform',
  ]);

  /**
   * Reports whose embedded view should default to a 90-day window when My Dashboard
   * first dispatches a date range (i.e. before the user has touched the picker).
   * Once the user actively picks any range from the dashboard picker, that
   * user-selected range flows through unchanged like every other widget.
   */
  private static readonly NINETY_DAY_DEFAULT_REPORT_KEYS: ReadonlySet<EmbeddedReportKey> = new Set([
    'social-platform',
    'seo-stats',
    'campaign-ads-stats',
    'training-dashboard',
    'practina-dashboard',
  ]);

  private prefersNinetyDayDefault(reportKey: EmbeddedReportKey): boolean {
    return MyDashboardComponent.NINETY_DAY_DEFAULT_REPORT_KEYS.has(reportKey);
  }

  /**
   * Builds a fresh "Last 90 Days" payload (LA timezone) on top of the picker's
   * current emission. Preserves `unit` / `comparison_type` from the base payload
   * and recomputes the comparison window using the same rules the picker uses
   * internally (previous-period = same-length window immediately before; previous-year = shifted -1y).
   */
  private buildNinetyDayDefaultPayload(basePayload: DateRangePickerOutput): DateRangePickerOutput {
    const end = dayjs().tz(LA_TIMEZONE).endOf('day');
    const start = end.subtract(89, 'day').startOf('day');

    let previousStartDate: string | null = null;
    let previousEndDate: string | null = null;

    if (basePayload.comparison_type === 'previous-period') {
      const days = end.diff(start, 'day');
      const comparisonEnd = start.subtract(1, 'day');
      const comparisonStart = comparisonEnd.subtract(days, 'day');
      previousStartDate = comparisonStart.utc().toISOString();
      previousEndDate = comparisonEnd.utc().toISOString();
    } else if (basePayload.comparison_type === 'previous-year') {
      previousStartDate = start.subtract(1, 'year').utc().toISOString();
      previousEndDate = end.subtract(1, 'year').utc().toISOString();
    }

    return {
      ...basePayload,
      start_date: start.utc().toISOString(),
      end_date: end.utc().toISOString(),
      previous_start_date: previousStartDate,
      previous_end_date: previousEndDate,
      preset_label: 'Last 90 Days',
    };
  }

  /**
   * Returns the payload that will be dispatched to an embedded report.
   *
   * The picker emits a `unit` that always reflects the user's current
   * groupBy selection (auto-derived from the chosen range, or explicitly
   * picked from the Day / Week / Month / Quarter / Year dropdown). Honor
   * that value as-is so every embedded module (Monthly Report 1/2, Social,
   * SEO, Campaign Ads, Training, etc.) receives the same granularity the
   * user just selected — e.g. selecting "All Time" or "Last 2 Years" no
   * longer collapses every widget back to a hard-coded `month` / `day`.
   *
   * Only fall back to a per-report sensible default when the picker omits
   * a unit (defensive — the picker always emits one in practice, but the
   * old behavior is preserved for reports that historically expected a
   * monthly cadence on their first paint).
   */
  private getPayloadWithDefaultUnit(
    reportKey: EmbeddedReportKey,
    basePayload: DateRangePickerOutput,
  ): any {
    const isMonthUnitComponent = MyDashboardComponent.MONTH_UNIT_REPORT_KEYS.has(reportKey);

    // Month-cadence reports (seo-stats, social-platform, campaign-ads-stats)
    // only support `month` / `quarter` server-side.
    // Coerce any incoming `day` / `week` / `year` (or missing) unit to
    // `month` so the embedded card never sends an unsupported granularity
    // like `?unit=day` that the backend rejects.
    if (isMonthUnitComponent) {
      const incomingUnit = (basePayload.unit ?? '').toString().toLowerCase();
      const safeUnit = MyDashboardComponent.MONTH_UNIT_ALLOWED_UNITS.has(incomingUnit)
        ? incomingUnit
        : 'month';
      return { ...basePayload, unit: safeUnit };
    }

    if (basePayload.unit) {
      return { ...basePayload };
    }
    return {
      ...basePayload,
      unit: 'day',
    };
  }

  onDashboardDateRangeChange(event: DateRangePickerOutput): void {
    this.dashboardDateEmitCount++;
    const isInitialAutoEmit = this.dashboardDateEmitCount === 1;
    this.currentDateRange.set(event);
    this.forEachReportRef((ref, reportKey) => {
      if (isInitialAutoEmit && this.prefersSelfSeededDateRange(reportKey)) {
        return;
      }
      this.hydratedReportRefs.add(ref);
      // Before the user has picked anything, force the 90-day default for the
      // designated reports; after that, use whatever the picker emitted.
      const sourcePayload = isInitialAutoEmit && this.prefersNinetyDayDefault(reportKey)
        ? this.buildNinetyDayDefaultPayload(event)
        : event;
      const customizedPayload = this.getPayloadWithDefaultUnit(reportKey, sourcePayload);
      this.dispatchDateRangeToRef(ref, customizedPayload);
    });
  }

  private hydrateReportRefs(): void {
    const dateRange = this.currentDateRange();
    if (!dateRange) return;
    const onlyAutoEmitSeen = this.dashboardDateEmitCount <= 1;
    this.forEachReportRef((ref, reportKey) => {
      if (this.hydratedReportRefs.has(ref)) return;
      if (onlyAutoEmitSeen && this.prefersSelfSeededDateRange(reportKey)) return;
      this.hydratedReportRefs.add(ref);
      // Late-mounted ref before the user has interacted with the picker — apply
      // the 90-day default for the designated reports.
      const sourcePayload = onlyAutoEmitSeen && this.prefersNinetyDayDefault(reportKey)
        ? this.buildNinetyDayDefaultPayload(dateRange)
        : dateRange;
      const customizedPayload = this.getPayloadWithDefaultUnit(reportKey, sourcePayload);
      this.dispatchDateRangeToRef(ref, customizedPayload);
    });
  }

  private prefersSelfSeededDateRange(reportKey: EmbeddedReportKey): boolean {
    return MyDashboardComponent.SELF_SEEDED_DATE_RANGE_KEYS.has(reportKey);
  }

  ngAfterViewInit(): void {
    this.hydrateReportRefs();
    this.setupHeaderCollapseObserver();
  }

  ngOnDestroy(): void {
    this.headerObserver?.disconnect();
    this.headerObserver = undefined;
  }

  /**
   * Wires an IntersectionObserver to a 1px sentinel sitting just above the
   * sticky header. The sentinel is intersected (visible) only while the page
   * is scrolled to the top; the instant it leaves the viewport we know the
   * sticky header has pinned and we flip `headerCollapsed` so the template
   * transitions into the compact, single-line layout. Using an observer
   * keeps the work off the scroll thread and avoids attaching a scroll
   * listener to the layout's scrollport.
   */
  private setupHeaderCollapseObserver(): void {
    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') return;
    const sentinel = this.headerSentinel?.nativeElement;
    if (!sentinel) return;

    this.headerObserver?.disconnect();
    this.headerObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        this.headerCollapsed.set(!entry.isIntersecting);
      },
      { threshold: 0, rootMargin: '0px' },
    );
    this.headerObserver.observe(sentinel);
  }

  ngOnInit() {
    // Drive the initial catalog/`/dashboards` load synchronously only
    // if the sidebar has already resolved the selected product. When it
    // hasn't (e.g. fresh hard-refresh into /my-dashboard before
    // sidebar.loadProducts() finishes), defer the load to the
    // constructor effect — it will fire exactly once when
    // `selectedProductId` flips from null to a real id. This eliminates
    // the previous wasted `/dashboards?product_id=<catalog-fallback>`
    // call that was always immediately superseded by the correct one.
    const initialProductId = this.apiService.selectedProductId();
    this.lastLoadedProductId = initialProductId;
    if (initialProductId != null) {
      this.hasFiredInitialLoad = true;
      this.loadCatalogData();
    }

    // ── DEBUG: expose this component's live state on `window.__myDashboard` 
    if (typeof window !== 'undefined') {
      (window as any).__myDashboard = {
        dashboards: () => this.dashboards(),
        filteredReports: () => this.filteredReports(),
        availableReports: () => this.availableReports(),
        visibleWidgets: () => this.visibleWidgets(),
        hasDashboardData: () => this.hasDashboardData(),
        searchQuery: () => this.searchQuery(),
        layoutLoading: () => this.layoutLoading(),
        visibleWidgetKeysForReport: (pid: number) => this.visibleWidgetKeysForReport(pid),
        reload: () => this.loadCatalogData(),
      };
    }
  }

  private resolveSelectedProductPid(): string | number | null {
    const fromSidebar = this.apiService.selectedProductPid();
    if (fromSidebar != null) return fromSidebar;

    const selectedId = this.apiService.selectedProductId();
    if (selectedId == null) return null;

    const products = this.apiService.products();
    const matchedFromProducts = products.find(
      (p: any) => String(p?.id) === String(selectedId),
    );
    if (matchedFromProducts?.pid != null) return matchedFromProducts.pid;

    try {
      const stored = sessionStorage.getItem('dynamic_routes');
      if (!stored) return null;
      const dynamicRoutes = JSON.parse(stored) as Array<any>;
      const matchedFromRoutes = dynamicRoutes.find(
        (entry: any) => String(entry?.product?.id) === String(selectedId),
      );
      return matchedFromRoutes?.product?.pid ?? null;
    } catch (err) {
      return null;
    }
  }

  private loadCatalogData() {
    const myVersion = ++this.loadCatalogVersion;

    this.layoutLoading.set(true);
    const selectedProductId = this.apiService.selectedProductId();
    const selectedProductPid = this.resolveSelectedProductPid();
    const isNumericProductId = (v: any): boolean =>
      v != null && /^\d+$/.test(String(v));

    type CatalogPageEntry = { product: any; page: any; nameKey: string };

    this.layoutService.getCatalogProducts().pipe(
      switchMap(catalog => {
        let activeProductPid: string | number | null = isNumericProductId(selectedProductPid)
          ? selectedProductPid
          : null;

        if (!isNumericProductId(activeProductPid) && selectedProductId != null) {
          const fromProductsList = (this.apiService.products() ?? []).find(
            (p: any) => String(p?.id) === String(selectedProductId),
          );
          const pidFromProducts = fromProductsList?.pid ?? null;
          if (isNumericProductId(pidFromProducts)) {
            activeProductPid = pidFromProducts;
          }
        }

        const filteredCatalog = isNumericProductId(activeProductPid)
          ? (catalog as any[]).filter((p: any) => String(p?.id) === String(activeProductPid))
          : (catalog as any[]);

        const grouped = new Map<string, CatalogPageEntry[]>();
        filteredCatalog.forEach((product: any) => {
          (product.pages || []).forEach((page: any) => {
            const nameKey = (page.name as string).toLowerCase().trim();
            const bucket = grouped.get(nameKey) ?? [];
            bucket.push({ product, page, nameKey });
            grouped.set(nameKey, bucket);
          });
        });

        const selectedEntries: CatalogPageEntry[] = [];
        const seenPageIds = new Set<number>();
        grouped.forEach((bucket, nameKey) => {
          const ranked = [...bucket].sort((a, b) => {
            const aNumeric = isNumericProductId(a.product?.id);
            const bNumeric = isNumericProductId(b.product?.id);
            if (aNumeric !== bNumeric) return aNumeric ? -1 : 1;
            if (aNumeric && bNumeric) {
              const aProd = Number(a.product?.id);
              const bProd = Number(b.product?.id);
              if (aProd !== bProd) return aProd - bProd;
            }
            return Number(a.page.id) - Number(b.page.id);
          });
          const chosen = ranked[0];
          if (!chosen) return;
          if (seenPageIds.has(chosen.page.id)) return;
          seenPageIds.add(chosen.page.id);
          selectedEntries.push(chosen);
          const dropped = ranked.slice(1).map(e => ({ pageId: e.page.id, productId: e.product?.id }));
          if (dropped.length > 0) {

          }
        });

        // ── Resolve the `product_id` query param for `/dashboards`.
        // activeProductPid show current selected product id that user selected in the sidebar (denefit=1, credee=5, …)
        let apiProductId: string | number | null = isNumericProductId(activeProductPid)
          ? activeProductPid
          : null;

        if (!isNumericProductId(apiProductId)) {
          const chosenForApi =
            selectedEntries.find(e => isNumericProductId(e.product?.id)) ?? selectedEntries[0];
          const fallbackFromSelected = chosenForApi?.product?.id ?? null;
          if (isNumericProductId(fallbackFromSelected)) {

            apiProductId = fallbackFromSelected;
          } else {
            const numericFallback = (catalog as any[]).find((p: any) => isNumericProductId(p?.id));
            if (numericFallback) {
              apiProductId = numericFallback.id;
            }
          }
        }

        // Single `/dashboards?platform=web&mixed=true&product_id=<id>` call. No `page_id` — backend returns all widgets across pages, and we group them client-side by `widget.source_page.id` below.

        const mixedLayout$ = isNumericProductId(apiProductId)
          ? this.layoutService.loadMixed<any>(
            apiProductId as string | number,
            'web',
            this.mixedEnabled(),
          )
          : of(null);

        if (!isNumericProductId(apiProductId)) {
          // console.warn(
          //   '[MyDashboard] Skipping mixed /dashboards call — apiProductId is not numeric:',
          //   apiProductId,
          // );
        }

        return mixedLayout$.pipe(
          map((mixedLayout: any) => ({
            catalog: filteredCatalog,
            fullCatalog: catalog as any[],
            selectedEntries,
            mixedLayout,
            apiProductId,
          }))
        );
      }),
      finalize(() => {
        if (myVersion === this.loadCatalogVersion) {
          this.layoutLoading.set(false);
        }
      })
    ).subscribe({
      next: ({ catalog, fullCatalog, selectedEntries, mixedLayout, apiProductId }) => {
        // ── Stale-response discard  override previous seletced product with new selected productand carries that response with api. 

        if (myVersion !== this.loadCatalogVersion) {
          // console.warn(
          //   '[MyDashboard] discarding stale catalog response — a newer loadCatalogData() superseded this one',
          //   {
          //     staleVersion: myVersion,
          //     currentVersion: this.loadCatalogVersion,
          //     staleApiProductId: apiProductId,
          //     currentSelectedProductId: this.apiService.selectedProductId(),
          //     currentSelectedProductPid: this.apiService.selectedProductPid(),
          //   },
          // );
          return;
        }

        const allMixedWidgets: any[] = mixedLayout?.widgets ?? [];
        const widgetsBySourcePage = new Map<number, any[]>();
        const widgetsByKey = new Map<string, any>();
        allMixedWidgets.forEach((w: any) => {
          if (w?.widget_key) widgetsByKey.set(w.widget_key, w);
          const srcPageId = Number(w?.source_page?.id ?? w?.source_page_id ?? 0);
          if (Number.isFinite(srcPageId) && srcPageId > 0) {
            const arr = widgetsBySourcePage.get(srcPageId) ?? [];
            arr.push(w);
            widgetsBySourcePage.set(srcPageId, arr);
          }
        });

        const apiDashboardId = mixedLayout?.dashboard?.id ?? null;
        const hasMixedData = !!apiDashboardId || allMixedWidgets.length > 0;
        this.hasDashboardData.set(hasMixedData);

        const mappedDashboards: Dashboard[] = [];

        selectedEntries.forEach(({ product, page, nameKey }) => {
          // (HIDDEN_PAGE_NAMES + per-name dedup already applied above.)
          void nameKey;

          const sourcePageMatches = widgetsBySourcePage.get(Number(page.id)) ?? [];
          const catalogWidgetKeys: string[] = (page.widgets || []).map((w: any) => w.widget_key);
          const keyMatches = catalogWidgetKeys
            .map((k: string) => widgetsByKey.get(k))
            .filter((w: any) => !!w);
          const layoutWidgets = sourcePageMatches.length > 0 ? sourcePageMatches : keyMatches;
          const isInitialEmpty = layoutWidgets.length === 0;

          const catalogKeys = (page.widgets || []).map((w: any) => w.widget_key);
          const layoutKeys = layoutWidgets.map((w: any) => w.widget_key);
          const layoutVisibilityByKey = layoutWidgets.map((w: any) => ({ key: w.widget_key, is_visible: w.is_visible, id: w.id }));
          // console.debug('[MyDashboard:debug] page mapping inputs', {
          //   pageId: page.id,
          //   pageName: page.name,
          //   productId: product?.id,
          //   apiDashboardId,
          //   isInitialEmpty,
          //   catalogKeyCount: catalogKeys.length,
          //   layoutKeyCount: layoutKeys.length,
          //   catalogKeys,
          //   layoutKeys,
          //   layoutVisibilityByKey,
          // });

          const widgetMap = new Map<string, {
            placementId: number;
            isVisible: boolean;
            colSpan: number | null;
            rowSpan: number | null;
            position: number;
            customConfig: Record<string, any> | null;
          }>();
          layoutWidgets.forEach((w: any) => {
            widgetMap.set(w.widget_key, {
              placementId: w.id,
              isVisible: w.is_visible !== false && w.is_visible !== 0 && w.is_visible !== '0' && w.is_visible !== 'false',
              colSpan: w.col_span ?? null,
              rowSpan: w.row_span ?? null,
              position: typeof w.position === 'number' ? w.position : 0,
              customConfig: (w.custom_config && typeof w.custom_config === 'object')
                ? w.custom_config
                : null,
            });
          });

          const mappedWidgets = (page.widgets || [])
            .filter((w: any) => w.is_visible !== false && w.is_visible !== 0 && w.is_visible !== '0' && w.is_visible !== 'false')
            .map((w: any, idx: number) => {
              const layoutEntry = widgetMap.get(w.widget_key);
              return {
                id: w.widget_key,
                title: resolveDashboardWidgetDisplayTitle(w.widget_key, w.name),
                icon: this.getIconForWidget(w.widget_key) as IconName,
                visible: layoutEntry ? layoutEntry.isVisible : false,
                isPinned: false,
                category: page.name,
                placementId: layoutEntry?.placementId,
                widgetApiId: w.id,
                colSpan: layoutEntry?.colSpan ?? null,
                rowSpan: layoutEntry?.rowSpan ?? null,
                position: layoutEntry?.position ?? idx,
                customConfig: layoutEntry?.customConfig ?? null,
              };
            });

          const lookupReport = mappedWidgets.map((w: any) => ({
            widget_key: w.id,
            hasLayoutEntry: widgetMap.has(w.id),
            visible: w.visible,
          }));
          mappedDashboards.push({
            id: `api-page-${page.id}`,
            name: page.name,
            icon: this.getIconForPage(page.name),
            isExpanded: mappedDashboards.length === 0,
            dashboardApiId: apiDashboardId,
            productId: product?.id ?? null,
            initialWidgetsEmpty: isInitialEmpty,
            widgets: mappedWidgets,
          });
        });
        const fullCatalogPageById = new Map<number, { product: any; page: any }>();
        const fullCatalogPageByWidgetKey = new Map<string, { product: any; page: any }>();
        (fullCatalog ?? []).forEach((p: any) => {
          (p?.pages ?? []).forEach((pg: any) => {
            const pid = Number(pg?.id);
            if (Number.isFinite(pid) && pid > 0 && !fullCatalogPageById.has(pid)) {
              fullCatalogPageById.set(pid, { product: p, page: pg });
            }
            (pg?.widgets ?? []).forEach((w: any) => {
              if (w?.widget_key && !fullCatalogPageByWidgetKey.has(w.widget_key)) {
                fullCatalogPageByWidgetKey.set(w.widget_key, { product: p, page: pg });
              }
            });
          });
        });

        const mappedPageIds = new Set<number>(
          mappedDashboards
            .map((d) => Number(d.id.replace('api-page-', '')))
            .filter((n) => Number.isFinite(n) && n > 0),
        );
        const mappedWidgetKeys = new Set<string>();
        mappedDashboards.forEach((d) => {
          d.widgets.forEach((w) => {
            if (w.placementId != null) mappedWidgetKeys.add(w.id);
          });
        });

        const orphanByPageId = new Map<number, any[]>();
        allMixedWidgets.forEach((w: any) => {
          if (!w?.widget_key) return;
          if (mappedWidgetKeys.has(w.widget_key)) return;

          let srcPageId = Number(w?.source_page?.id ?? w?.source_page_id ?? 0);
          if (!Number.isFinite(srcPageId) || srcPageId <= 0) {
            const byKey = fullCatalogPageByWidgetKey.get(w.widget_key);
            if (byKey) srcPageId = Number(byKey.page?.id ?? 0);
          }
          if (!Number.isFinite(srcPageId) || srcPageId <= 0) return;
          if (mappedPageIds.has(srcPageId)) return;

          const arr = orphanByPageId.get(srcPageId) ?? [];
          arr.push(w);
          orphanByPageId.set(srcPageId, arr);
        });

        if (orphanByPageId.size > 0) {
          // console.warn(
          //   '[MyDashboard] orphan widgets detected — synthesizing page entries from full catalog',
          //   Array.from(orphanByPageId.entries()).map(([pageId, ws]) => ({
          //     pageId,
          //     widgetKeys: ws.map((w: any) => w.widget_key),
          //   })),
          // );
        }

        orphanByPageId.forEach((orphanWidgets, pageId) => {
          const catalogEntry = fullCatalogPageById.get(pageId);
          if (!catalogEntry) {
            return;
          }

          const { product: orphanProduct, page: orphanPage } = catalogEntry;

          const orphanWidgetMap = new Map<
            string,
            {
              placementId: number;
              isVisible: boolean;
              colSpan: number | null;
              rowSpan: number | null;
              position: number;
              customConfig: Record<string, any> | null;
            }
          >();
          orphanWidgets.forEach((w: any) => {
            orphanWidgetMap.set(w.widget_key, {
              placementId: w.id,
              isVisible: w.is_visible !== false && w.is_visible !== 0 && w.is_visible !== '0' && w.is_visible !== 'false',
              colSpan: w.col_span ?? null,
              rowSpan: w.row_span ?? null,
              position: typeof w.position === 'number' ? w.position : 0,
              customConfig: (w.custom_config && typeof w.custom_config === 'object')
                ? w.custom_config
                : null,
            });
          });

          const orphanMappedWidgets = (orphanPage.widgets || []).map(
            (w: any, idx: number) => {
              const layoutEntry = orphanWidgetMap.get(w.widget_key);
              return {
                id: w.widget_key,
                title: resolveDashboardWidgetDisplayTitle(w.widget_key, w.name),
                icon: this.getIconForWidget(w.widget_key) as IconName,
                visible: layoutEntry ? layoutEntry.isVisible : false,
                isPinned: false,
                category: orphanPage.name,
                placementId: layoutEntry?.placementId,
                widgetApiId: w.id,
                colSpan: layoutEntry?.colSpan ?? null,
                rowSpan: layoutEntry?.rowSpan ?? null,
                position: layoutEntry?.position ?? idx,
                customConfig: layoutEntry?.customConfig ?? null,
              };
            },
          );

          mappedDashboards.push({
            id: `api-page-${orphanPage.id}`,
            name: orphanPage.name,
            icon: this.getIconForPage(orphanPage.name),
            isExpanded: false,
            dashboardApiId: apiDashboardId,
            productId: orphanProduct?.id ?? null,
            initialWidgetsEmpty: false,
            widgets: orphanMappedWidgets,
          });
          mappedPageIds.add(pageId);
        });

        // ── Re-align `availableReports.pageId` to whatever backend page.id 
        const dashboardByName = new Map<string, Dashboard>();
        mappedDashboards.forEach(db => dashboardByName.set(db.name.toLowerCase().trim(), db));
        this.availableReports.update(reports => reports.map(r => {
          const matched = dashboardByName.get(r.title.toLowerCase().trim());
          if (!matched) return r;
          const matchedPageId = Number(matched.id.replace('api-page-', ''));
          if (Number.isFinite(matchedPageId) && matchedPageId !== r.pageId) {

            return { ...r, pageId: matchedPageId };
          }
          return r;
        }));
        const currentPid = this.apiService.selectedProductPid();
        if (
          isNumericProductId(apiProductId) &&
          String(currentPid ?? '') !== String(apiProductId)
        ) {

          this.apiService.setSelectedProductPid(apiProductId);
        }

        // ── Fix: always reset stale tile order + stale report refs before
        // applying the new catalog state so no orphaned keys or dead widget
        // instances survive a reload (caused data-not-loading after hide/show).
        // However, we MUST NOT blindly clear reportRefs, because Angular might 
        // simply reorder existing WidgetHostComponent DOM nodes without destroying
        // them. If we clear reportRefs, we lose tracking of those surviving widgets.

        if (mappedDashboards.length > 0) {
          this.dashboards.set(mappedDashboards);
          this._syncUnifiedTilesOrderFromApi();
        } else {
          this.dashboards.set([]);
          this.unifiedTilesOrder.set([]);
        }

        // Prune orphaned keys from reportRefs to prevent memory leaks or stale updates
        const validKeys = new Set(this.unifiedTilesOrder());
        this.reportRefs.update((prev) => {
          const next = new Map(prev);
          for (const key of next.keys()) {
            if (!validKeys.has(key)) {
              next.delete(key);
            }
          }
          return next;
        });
        const visibilitySummary = mappedDashboards.map(db => ({
          page: db.name,
          pageId: db.id,
          total: db.widgets.length,
          visible: db.widgets.filter(w => w.visible).length,
          visibleKeys: db.widgets.filter(w => w.visible).map(w => w.id),
        }));
        const snapshot = new Map<string, boolean>();
        mappedDashboards.forEach(db => {
          db.widgets.forEach(w => snapshot.set(`${db.id}|${w.id}`, w.visible));
        });
        this.initialVisibilitySnapshot.set(snapshot);
      },
      error: (err) => {
        if (myVersion !== this.loadCatalogVersion) {

          return;
        }
        // console.error('Failed to load catalog data', err);
        this.dashboards.set([]);
        this.hasDashboardData.set(false);
      }
    });
  }

  resolveReportKey(pageName: string, widgetKey?: string): EmbeddedReportKey | null {
    const n = (pageName || '').toLowerCase().trim();
    if (n.includes('monthly report 1')) return 'monthly-report-1';
    if (n.includes('monthly report 2')) return 'monthly-report-2';
    if (n.includes('social media') || n.includes('social platform')) return 'social-platform';
    if (n.includes('seo stats')) return 'seo-stats';
    if (n.includes('ads overview') || n.includes('seo overview')) return 'seo-overview';
    if (n.includes('campaign ads') || n.includes('campaign ad')) return 'campaign-ads-stats';
    if (n.includes('industry') && n.includes('categor')) return 'industry-categorization';
    if (n.includes('training') && n.includes('demo')) return 'training-dashboard';
    if (n.includes('training dashboard')) return 'training-dashboard';
    if (n.includes('business performance')) return 'business-performance-report';
    if (n.includes('contracts report') || n.includes('contract report')) return 'contracts-report';
    if (
      n.includes('analytical-dashboard-widgets') ||
      n.includes('analytical dashboard widgets') ||
      n.includes('practina')
    ) {
      return 'practina-dashboard';
    }
    if (n.includes('lendee dashboard')) return 'lendee-dashboard';
    if (n.includes('lendee analytics')) return 'lendee-analytics-dashboard';
    if (
      n.includes('growth') &&
      (n.includes('activation') || n.includes('revenue'))
    ) {
      return 'growth-activation-and-revenue';
    }
    if (n.includes('cool credit')) return 'cool-credit-dashboard';
    if (
      n.includes('daily cron') ||
      n.includes('try again cron') ||
      n.includes('daily-cron-payments')
    ) {
      return 'daily-cron-payments';
    }
    if (
      n.includes('overdue cron') ||
      n.includes('friday cron') ||
      n.includes('overdue-cron-payments')
    ) {
      return 'overdue-cron-payments';
    }
    if (
      n.includes('arrangement cron') &&
      n.includes('admin performance')
    ) {
      return 'arrangement-cron-admin-performance';
    }
    if (
      n.includes('arrangement cron') &&
      (n.includes('trends') || n.includes('recovery'))
    ) {
      return 'arrangement-cron-trends';
    }
    if (
      n.includes('arrangement cron') &&
      (n.includes('overview table') || n.includes('overview'))
    ) {
      return 'arrangement-cron-overview';
    }

    const fromWidgetKey = this.resolveReportKeyFromWidgetKey(widgetKey);
    if (fromWidgetKey) return fromWidgetKey;

    for (const [pageKey, hints] of Object.entries(CATALOG_PAGE_HINTS) as [
      DashboardPageKey,
      string[],
    ][]) {
      if (!hints?.length) continue;
      if (hints.some((h) => n.includes(h))) {
        return pageKey as EmbeddedReportKey;
      }
    }

    return this.resolveReportKeyFromWidgetKey(widgetKey);
  }

  /** Fallback when catalog page names don't match — infer from `vb_*` widget_key. */
  private resolveReportKeyFromWidgetKey(
    widgetKey?: string,
  ): EmbeddedReportKey | null {
    if (!widgetKey) return null;
    const canonical = stripProductPrefix(widgetKey);
    if (CONTRACTS_REPORT_WIDGET_KEYS.has(widgetKey) || CONTRACTS_REPORT_WIDGET_KEYS.has(canonical)) return 'contracts-report';
    if (widgetKey.startsWith('vb_') || canonical.startsWith('vb_')) return 'business-performance-report';
    if (widgetKey.startsWith('pr_an_') || canonical.startsWith('an_') || canonical.startsWith('pr_an_')) return 'practina-dashboard';
    if (LENDEE_DASHBOARD_WIDGET_KEYS.has(widgetKey) || LENDEE_DASHBOARD_WIDGET_KEYS.has(canonical)) return 'lendee-dashboard';
    if (LENDEE_ANALYTICS_WIDGET_KEYS.has(widgetKey) || LENDEE_ANALYTICS_WIDGET_KEYS.has(canonical)) return 'lendee-analytics-dashboard';
    if (widgetKey.startsWith('lend_') || canonical.startsWith('lend_')) return 'lendee-dashboard';
    if (widgetKey.startsWith('ld_') || canonical.startsWith('ld_')) return 'lendee-analytics-dashboard';
    if (COOL_CREDIT_GROWTH_WIDGET_KEYS.has(widgetKey) || COOL_CREDIT_GROWTH_WIDGET_KEYS.has(canonical)) return 'growth-activation-and-revenue';
    if (
      widgetKey.startsWith('co_ga_') ||
      widgetKey.startsWith('cc_gar_') ||
      canonical.startsWith('co_ga_') ||
      canonical.startsWith('cc_gar_') ||
      canonical.startsWith('ga_') ||
      canonical.startsWith('gar_')
    ) {
      return 'growth-activation-and-revenue';
    }
    if (COOL_CREDIT_DASHBOARD_WIDGET_KEYS.has(widgetKey) || COOL_CREDIT_DASHBOARD_WIDGET_KEYS.has(canonical)) return 'cool-credit-dashboard';
    if (
      widgetKey.startsWith('co_cc_') ||
      canonical.startsWith('co_cc_') ||
      canonical.startsWith('cc_')
    ) {
      return 'cool-credit-dashboard';
    }
    if (
      DAILY_CRON_WIDGET_KEYS.has(widgetKey) ||
      DAILY_CRON_WIDGET_KEYS.has(canonical) ||
      widgetKey.startsWith('dc_') ||
      canonical.startsWith('dc_')
    ) {
      return 'daily-cron-payments';
    }
    if (
      OVERDUE_CRON_WIDGET_KEYS.has(widgetKey) ||
      OVERDUE_CRON_WIDGET_KEYS.has(canonical) ||
      widgetKey.startsWith('oc_') ||
      canonical.startsWith('oc_')
    ) {
      return 'overdue-cron-payments';
    }
    if (
      ARRANGEMENT_CRON_ADMIN_PERFORMANCE_WIDGET_KEYS.has(widgetKey) ||
      ARRANGEMENT_CRON_ADMIN_PERFORMANCE_WIDGET_KEYS.has(canonical)
    ) {
      return 'arrangement-cron-admin-performance';
    }
    if (
      ARRANGEMENT_CRON_TRENDS_WIDGET_KEYS.has(widgetKey) ||
      ARRANGEMENT_CRON_TRENDS_WIDGET_KEYS.has(canonical)
    ) {
      return 'arrangement-cron-trends';
    }
    if (
      ARRANGEMENT_CRON_OVERVIEW_WIDGET_KEYS.has(widgetKey) ||
      ARRANGEMENT_CRON_OVERVIEW_WIDGET_KEYS.has(canonical)
    ) {
      return 'arrangement-cron-overview';
    }
    return null;
  }

  isBlackCard(tile: VisibleWidgetTile): boolean {
    const key = tile.reportKey;
    return (
      key === 'daily-cron-payments' ||
      key === 'overdue-cron-payments' ||
      key === 'arrangement-cron-overview' ||
      key === 'arrangement-cron-trends' ||
      key === 'arrangement-cron-admin-performance'
    );
  }

  /** Short chip label for the customize-layout preview tiles. */
  private customizeSourceChip(reportKey: EmbeddedReportKey | null, pageName: string): string {
    switch (reportKey) {
      case 'monthly-report-1': return 'MO';
      case 'monthly-report-2': return 'CI';
      case 'social-platform': return 'Social';
      case 'seo-stats': return 'SEO';
      case 'seo-overview': return 'Ads';
      case 'campaign-ads-stats': return 'Campaign';
      case 'industry-categorization': return 'Industry';
      case 'training-dashboard': return 'Training';
      case 'business-performance-report': return 'Business';
      case 'contracts-report': return 'Contracts';
      case 'practina-dashboard': return 'Practina';
      case 'lendee-dashboard': return 'Lendee';
      case 'lendee-analytics-dashboard': return 'Lendee Analytics';
      case 'cool-credit-dashboard': return 'Cool Credit';
      case 'growth-activation-and-revenue': return 'CC Growth';
      case 'daily-cron-payments': return 'Daily Cron';
      case 'overdue-cron-payments': return 'Overdue';
      case 'arrangement-cron-overview': return 'Arrangement';
      case 'arrangement-cron-trends': return 'AC Trends';
      case 'arrangement-cron-admin-performance': return 'AC Admin';
      default: {
        const words = (pageName || 'Report').split(/\s+/).filter(Boolean);
        return words.slice(0, 2).map((w) => w[0]?.toUpperCase() ?? '').join('') || 'RPT';
      }
    }
  }

  private getIconForPage(name: string): IconName {
    const lowerName = name.toLowerCase();
    if (lowerName.includes('monthly report 1')) return 'chartBar';
    if (lowerName.includes('monthly report 2')) return 'chartPie';
    if (lowerName.includes('seo')) return 'trendingUp';
    if (lowerName.includes('social')) return 'smartphone';
    if (lowerName.includes('training')) return 'users';
    if (lowerName.includes('campaign')) return 'target';
    if (lowerName.includes('industry')) return 'building2';
    if (lowerName.includes('ads overview') || lowerName.includes('seo overview')) return 'target';
    if (lowerName.includes('business performance') || lowerName.includes('visionboard-business')) {
      return 'building2';
    }
    if (lowerName.includes('contracts report') || lowerName.includes('visionboard-contracts')) {
      return 'target';
    }
    if (
      lowerName.includes('analytical-dashboard-widgets') ||
      lowerName.includes('practina')
    ) {
      return 'activity';
    }
    if (lowerName.includes('lendee')) return 'chartBar';
    if (lowerName.includes('cool credit')) return 'chartBar';
    if (
      lowerName.includes('growth') &&
      (lowerName.includes('activation') || lowerName.includes('revenue'))
    ) {
      return 'chartLine';
    }
    if (lowerName.includes('daily cron') || lowerName.includes('try again cron')) {
      return 'chartLine';
    }
    if (lowerName.includes('overdue cron') || lowerName.includes('friday cron')) {
      return 'chartLine';
    }
    if (
      lowerName.includes('arrangement cron') &&
      lowerName.includes('admin performance')
    ) {
      return 'chartLine';
    }
    if (
      lowerName.includes('arrangement cron') &&
      (lowerName.includes('trends') || lowerName.includes('recovery'))
    ) {
      return 'chartLine';
    }
    if (
      lowerName.includes('arrangement cron') &&
      lowerName.includes('overview')
    ) {
      return 'chartLine';
    }
    return 'activity';
  }

  /* Picks a sensible icon for an individual widget based on its backend `widget_key`. Used as the small icon on each tile in the flat reports */
  private getIconForWidget(widgetKey: string): IconName {
    const key = (widgetKey || '').toLowerCase();
    if (key.includes('pie') || key.includes('contract_type')) return 'chartPie';
    if (key.includes('line') || key.includes('mom') || key.includes('trend')) return 'chartLine';
    if (key.includes('bar') || key.includes('top_') || key.includes('categor')) return 'chartBar';
    if (key.includes('table') || key.includes('overall')) return 'list';
    if (key.includes('country') || key.includes('region') || key.includes('enrol')) return 'building2';
    if (key.includes('stat') || key.includes('kpi')) return 'target';
    if (key.includes('training') || key.includes('admin')) return 'users';
    if (key.includes('seo') || key.includes('keyword')) return 'trendingUp';
    if (key.includes('social') || key.includes('platform')) return 'smartphone';
    return 'activity';
  }

  filteredDashboards = computed(() => {
    const query = this.searchQuery().toLowerCase().trim();
    if (!query) return this.dashboards();
    return this.dashboards().map(db => ({
      ...db,
      widgets: db.widgets.filter(w =>
        w.title.toLowerCase().includes(query) ||
        db.name.toLowerCase().includes(query)
      )
    })).filter(db => db.widgets.length > 0 || db.name.toLowerCase().includes(query));
  });

  filteredReports = computed(() => {
    const query = this.searchQuery().toLowerCase().trim();
    const dbs = this.dashboards();
    const decisions: Array<{ id: string; pageId: number | undefined; reason: string; kept: boolean }> = [];
    const result = this.availableReports().filter(r => {
      if (query && !r.title.toLowerCase().includes(query)) {
        decisions.push({ id: r.id, pageId: r.pageId, reason: 'search-mismatch', kept: false });
        return false;
      }
      if (r.pageId != null) {
        const db = dbs.find(d => d.id === `api-page-${r.pageId}`);
        if (!db) {
          decisions.push({ id: r.id, pageId: r.pageId, reason: `no dashboard for api-page-${r.pageId} in dashboards()`, kept: false });
          return false;
        }
        const hasVisible = db.widgets.some(w => w.visible);
        decisions.push({
          id: r.id,
          pageId: r.pageId,
          reason: hasVisible ? 'has-visible-widget' : `no widget with visible=true (total=${db.widgets.length})`,
          kept: hasVisible,
        });
        return hasVisible;
      }
      decisions.push({ id: r.id, pageId: r.pageId, reason: 'pageId-missing', kept: false });
      return false;
    });

    return result;
  });

  visibleWidgets = computed<VisibleWidgetTile[]>(() => {
    const query = this.searchQuery().toLowerCase().trim();
    const tiles: VisibleWidgetTile[] = [];

    for (const db of this.dashboards()) {
      const pageId = db.id.startsWith('api-page-')
        ? Number(db.id.replace('api-page-', ''))
        : null;
      for (const w of db.widgets) {
        if (!w.visible) continue;
        if (EXCLUDED_WIDGET_KEYS.has(w.id)) continue;
        if (query) {
          const inWidget = w.title.toLowerCase().includes(query);
          const inPage = db.name.toLowerCase().includes(query);
          if (!inWidget && !inPage) continue;
        }

        tiles.push({
          ...w,
          dashboardId: db.id,
          pageName: db.name,
          reportKey: this.resolveReportKey(db.name, w.id),
          pageId,
          productId: db.productId ?? null,
          uniqueKey: `${db.id}::${w.id}::${w.placementId ?? w.widgetApiId ?? 'p'}`,
        });
      }
    }

    return tiles.sort((a, b) => {
      const ap = a.position ?? 0;
      const bp = b.position ?? 0;
      if (ap !== bp) return ap - bp;
      if (a.dashboardId !== b.dashboardId) {
        return a.dashboardId.localeCompare(b.dashboardId);
      }
      const aPid = a.placementId ?? a.widgetApiId ?? 0;
      const bPid = b.placementId ?? b.widgetApiId ?? 0;
      return aPid - bPid;
    });
  });

  /** Stable track-by for the unified widget grid. */
  trackWidgetTile = (_idx: number, tile: VisibleWidgetTile): string => tile.uniqueKey;
  private unifiedTilesOrder = signal<string[]>([]);
  readonly unifiedTiles = computed<VisibleWidgetTile[]>(() => {
    const tiles = this.visibleWidgets();
    const manualOrder = this.unifiedTilesOrder();
    if (manualOrder.length === 0) return tiles;

    const byKey = new Map(tiles.map((t) => [t.uniqueKey, t] as const));
    const seen = new Set<string>();
    const ordered: VisibleWidgetTile[] = [];

    // ── Fix: only include keys that are still present in visibleWidgets()
    // (stale keys from a previous load would skip the byKey lookup but the
    // tile would still appear via the fallback loop, creating duplicates).
    for (const key of manualOrder) {
      const tile = byKey.get(key);
      if (tile && !seen.has(key)) {
        ordered.push(tile);
        seen.add(key);
      }
    }
    // Append any tiles that arrived after the manual order was last persisted
    // (e.g. a newly-added card from a component page) without duplicating.
    for (const t of tiles) {
      if (!seen.has(t.uniqueKey)) ordered.push(t);
    }
    return ordered;
  });

  /** track-by for the unified `@for` loop (mirrors `trackWidgetTile`). */
  trackUnifiedTile = (_idx: number, tile: VisibleWidgetTile): string => tile.uniqueKey;

  onUnifiedDrop(event: CdkDragDrop<VisibleWidgetTile[]>): void {
    if (event.previousIndex === event.currentIndex) return;
    const next = this.unifiedTiles().map((t) => t.uniqueKey);
    moveItemInArray(next, event.previousIndex, event.currentIndex);
    this.unifiedTilesOrder.set(next);
  }
  /* Number of visible widgets for a given catalog page id. */
  visibleWidgetCount(pageId: number | undefined): number {
    if (pageId == null) return 0;
    const db = this.dashboards().find(d => d.id === `api-page-${pageId}`);
    return db?.widgets.filter(w => {
      if (!w.visible || EXCLUDED_WIDGET_KEYS.has(w.id)) return false;

      return true;
    }).length ?? 0;
  }

  widgetsForReport(pageId: number | undefined): VisibleWidgetTile[] {
    if (pageId == null) return [];
    return this.visibleWidgets().filter(w => w.pageId === pageId);
  }

  visibleWidgetKeysForReport(pageId: number | undefined): string[] {
    if (pageId == null) return [];
    return this.widgetsForReport(pageId).map(w => w.id);
  }

  dashboardDiagnostics = computed(() => {
    const dbs = this.dashboards();
    let total = 0;
    let visible = 0;
    for (const db of dbs) {
      total += db.widgets.length;
      visible += db.widgets.filter(w => {
        if (!w.visible || EXCLUDED_WIDGET_KEYS.has(w.id)) return false;

        return true;
      }).length;
    }
    return { pages: dbs.length, total, visible };
  });
  resolveTileColSpan(widget: { id?: string; colSpan?: number | null }): number {
    if (!widget.id) return 12;
    return resolveUnifiedWidgetColSpan(
      widget.id,
      ON_SCREEN_COL_SPAN_BY_WIDGET_KEY,
      widget.colSpan,
    );
  }

  /**
   * Tailwind col-span (and, for the single tall-tile case, row-span) classes
   * for unified tiles — mirrors the popup preview grid.
   *
   * The only widget that opts into row-span on the unified grid is
   * "Top Categories by Contracts" rendered at its designed col-span 8.
   * That packing exactly matches MR1's standalone layout
   * (`SECTION_COL_CLASSES[TOP_CATEGORIES_BY_CONTRACTS] = '… lg:row-span-2'`),
   * so when the user added Top Categories plus two col-span-4 cards
   * (e.g. Enrolled Businesses + Contracts Created), those two narrow
   * cards stack on the right side instead of one of them being pushed
   * down to a new row.
   *
   * Every other widget keeps its current single-row behaviour — no
   * existing tile gets a row-span hint here.
   */
  getWidgetColClass(widget: { id?: string; colSpan?: number | null }): string {
    const colSpan = this.resolveTileColSpan(widget);
    const colClass = getUnifiedDashboardTileColClass(colSpan);
    if (this.isTallTileAtColSpan8(widget.id, colSpan)) {
      return `${colClass} lg:row-span-2`;
    }
    const cleanId = widget.id ? stripProductPrefix(widget.id) : '';
    if (cleanId === 'mr2_training_status' && colSpan === 4) {
      const tiles = this.unifiedTiles();
      const statsVisible = tiles.some(t => {
        const tId = t.id ? stripProductPrefix(t.id) : '';
        return tId === 'mr2_stats_overview' && this.resolveTileColSpan(t) > 4;
      });
      const momVisible = tiles.some(t => {
        const tId = t.id ? stripProductPrefix(t.id) : '';
        return tId === 'mr2_mom_activity' && this.resolveTileColSpan(t) > 4;
      });
      if (statsVisible && momVisible) {
        return `${colClass} lg:row-span-2`;
      }
    }
    if ((cleanId === 'ga_cbc_fetch_outcomes' || cleanId === 'gar_cbc_effect') && colSpan === 8) {
      const tiles = this.unifiedTiles();
      const hasPayment = tiles.some(t => {
        const tId = t.id ? stripProductPrefix(t.id) : '';
        return tId === 'ga_payment_method_distribution' || tId === 'gar_payment_method';
      });
      const hasSimulator = tiles.some(t => {
        const tId = t.id ? stripProductPrefix(t.id) : '';
        return tId === 'ga_simulator_usage_distribution' || tId === 'gar_simulator_usage';
      });
      if (hasPayment && hasSimulator) {
        return `${colClass} lg:row-span-2`;
      }
    }
    return colClass;
  }

  /**
   * Returns `true` when a widget key is in the tall-tile allow-list AND its
   * current col-span is exactly 8 — the only combination that opts the tile
   * into `row-span-2` on the unified grid. Accepts product-prefixed keys
   * (e.g. `de_mr1_top_categories_contracts`) by stripping the prefix via the
   * shared catalog helper.
   */
  private isTallTileAtColSpan8(widgetId: string | null | undefined, colSpan: number): boolean {
    if (!widgetId) return false;
    if (colSpan !== 8) return false;
    if (TALL_TILE_WIDGET_KEYS_AT_COLSPAN_8.has(widgetId)) return true;
    return TALL_TILE_WIDGET_KEYS_AT_COLSPAN_8.has(stripProductPrefix(widgetId));
  }

  toggleAccordion(dashboardId: string) {
    this.dashboards.update(dbs =>
      dbs.map(db => ({
        ...db,
        isExpanded: db.id === dashboardId ? !db.isExpanded : false
      }))
    );
  }

  toggleWidgetVisibility(dashboardId: string, widgetId: string) {
    // Local update only — API is called on "Apply Layout"
    this.dashboards.update(dbs =>
      dbs.map(d => {
        if (d.id === dashboardId) {
          return {
            ...d,
            widgets: d.widgets.map(w => w.id === widgetId ? { ...w, visible: !w.visible } : w)
          };
        }
        return d;
      })
    );
  }

  toggleWidgetPin(dashboardId: string, widgetId: string) {
    this.dashboards.update(dbs =>
      dbs.map(db => {
        if (db.id === dashboardId) {
          return {
            ...db,
            widgets: db.widgets.map(w => w.id === widgetId ? { ...w, isPinned: !w.isPinned } : w)
          };
        }
        return db;
      })
    );
  }

  onWidgetDrop(event: CdkDragDrop<Widget[]>, dashboardId: string) {
    this.dashboards.update(dbs =>
      dbs.map(db => {
        if (db.id === dashboardId) {
          const newWidgets = [...db.widgets];
          moveItemInArray(newWidgets, event.previousIndex, event.currentIndex);
          return { ...db, widgets: newWidgets };
        }
        return db;
      })
    );
  }

  applyLayout() {
    const isDashboardEmpty = this.dashboards().some(d => d.initialWidgetsEmpty || !d.dashboardApiId);

    const visibleWidgets = this.dashboards().flatMap(d =>
      d.widgets.filter(w => w.visible).map(w => ({
        widgetApiId: w.widgetApiId,
        sourcePageId: d.id.startsWith('api-page-') ? Number(d.id.replace('api-page-', '')) : null,
        productId: d.productId ?? null,
        colSpan: this.resolveTileColSpan(w),
        customConfig: w.customConfig ?? null,
      }))
    );

    const isNumericProductIdInner = (v: any): boolean =>
      v != null && /^\d+$/.test(String(v));
    const activePid = this.apiService.selectedProductPid();
    const activeCuid = this.apiService.selectedProductId();
    const topLevelProductId =
      (isNumericProductIdInner(activePid) ? activePid : null) ??
      (activeCuid != null ? activeCuid : null) ??
      this.dashboards().find((d) => d.productId != null)?.productId ??
      null;

    if (isDashboardEmpty) {
      // Call createDashboard
      const payload = {
        name: 'My Dashboard',
        type: 'mixed' as const,
        platform: 'web' as const,
        page_id: null,
        product_id: topLevelProductId,
        widget_ids: visibleWidgets.map(w => ({
          widget_id: Number(w.widgetApiId),
          source_page_id: w.sourcePageId,
          col_span: w.colSpan,
          row_span: 1,
          custom_config: w.customConfig ?? {}
        }))
      };

      this.layoutLoading.set(true);
      this.layoutService.createDashboard(payload)
        .pipe(
          finalize(() => this.layoutLoading.set(false)),
          catchError(err => {
            this.toaster.error(err?.error?.message || 'Failed to apply layout. Please try again.');
            return EMPTY;
          })
        )
        .subscribe((res) => {
          if (res) {
            this.toaster.success('Custom dashboard layout created and activated successfully!');
            this.loadCatalogData(); // snapshot is reset inside loadCatalogData
          }
        });
    } else {
      // Delta fallback sync to make sure everything matches
      const uniqueDashboardIds = Array.from(new Set(this.dashboards().map(d => d.dashboardApiId).filter(id => id != null)));

      if (uniqueDashboardIds.length > 0) {
        this.layoutLoading.set(true);
        const batchRequests = uniqueDashboardIds.map(dashboardId => {
          const targetWidgets = this.dashboards()
            .filter(d => d.dashboardApiId === dashboardId)
            .flatMap(d =>
              d.widgets.filter(w => w.visible).map(w => ({
                widget_id: Number(w.widgetApiId),
                source_page_id: d.id.startsWith('api-page-') ? Number(d.id.replace('api-page-', '')) : null,
                product_id: d.productId ?? null,
                col_span: this.resolveTileColSpan(w),
                row_span: 1,
                custom_config: w.customConfig ?? {}
              }))
            );
          return this.layoutService.batchUpdateWidgets(dashboardId!, { widgets: targetWidgets }).pipe(
            catchError(err => {
              return of(null);
            })
          );
        });

        forkJoin(batchRequests)
          .pipe(finalize(() => this.layoutLoading.set(false)))
          .subscribe(() => {
            this.toaster.success('Successfully synchronized your dashboard widgets!');
            this.loadCatalogData(); // snapshot is reset inside loadCatalogData
          });
      } else {
        this.toaster.success('Successfully synchronized your dashboard widgets!');
      }
    }
  }

  openCreateDashboardDialog() {
    const dialogRef = this.dialog.open(DashboardCreateDialogComponent, {
      width: '95vw',
      maxWidth: '650px',
      panelClass: 'custom-dialog-container',
      autoFocus: false,
    });

    dialogRef.afterClosed().subscribe((saved) => {
      if (saved) {
        this.loadCatalogData();
      }
    });
  }

  openAddWidgetDialog(db: any) {
    const pageId = db.id.startsWith('api-page-') ? Number(db.id.replace('api-page-', '')) : null;
    const alreadyExistingKeys = db.widgets.filter((w: any) => w.visible).map((w: any) => w.id);
    const currentWidgets = db.widgets.filter((w: any) => w.visible).map((w: any) => ({
      id: Number(w.widgetApiId || w.placementId || 0),
      pageId: pageId,
      colSpan: 12
    }));

    const dialogRef = this.dialog.open(DashboardAddWidgetDialogComponent, {
      width: '95vw',
      maxWidth: '650px',
      panelClass: 'custom-dialog-container',
      autoFocus: false,
      data: {
        dashboardApiId: db.dashboardApiId ?? null,
        isMixed: false,
        pageId: pageId,
        alreadyExistingKeys: alreadyExistingKeys,
        currentWidgets: currentWidgets,
        dashboardName: db.name
      }
    });

    dialogRef.afterClosed().subscribe((saved) => {
      if (saved) {
        this.loadCatalogData();
      }
    });
  }

  setView(view: 'reports' | 'management') {
    this.currentView.set(view);
  }

  setCardsView() {
    const dialogRef = this.dialog.open(CustomizeDashboardStateDialogComponent, {
      width: '95vw',
      maxWidth: '1100px',
      panelClass: ['custom-dialog-container', 'customize-state-dialog-panel'],
      backdropClass: 'customize-state-dialog-backdrop',
      autoFocus: false,
      data: {
        tiles: this.buildCustomizeTilesFromVisibleWidgets(),
        defaultTiles: this.buildDefaultCustomizeTiles(),
        mixed: this.mixedEnabled(),
      } as CustomizeDashboardStateDialogData & { mixed: boolean },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result) return;

      const isApplyResult = (
        v: any,
      ): v is CustomizeDashboardStateDialogApplyResult =>
        v && typeof v === 'object' && Array.isArray(v.tiles);

      const tiles = isApplyResult(result) ? result.tiles : result;
      const nextMixed = isApplyResult(result) ? result.mixed : null;

      // Allow an empty `tiles` array through — it represents the "last tile
      // removed" case from the popup's per-tile Remove button. Returning early
      // here would swallow the deletion and skip the PATCH call entirely.
      if (!Array.isArray(tiles)) return;

      if (nextMixed !== null && nextMixed !== this.mixedEnabled()) {
        this.mixedEnabled.set(nextMixed);
      }

      this.persistCustomizeTiles(tiles);
      this.setView('management');
    });
  }

  private persistCustomizeTiles(tiles: CustomizeDashboardPreviewTile[]): void {
    const tileByUniqueKey = new Map<string, CustomizeDashboardPreviewTile>();
    const tileOrderByUniqueKey = new Map<string, number>();
    tiles.forEach((t, idx) => {
      tileByUniqueKey.set(t.id, t);
      tileOrderByUniqueKey.set(t.id, idx);
    });

    const widgetUniqueKey = (db: Dashboard, w: Widget): string =>
      `${db.id}::${w.id}::${w.placementId ?? w.widgetApiId ?? 'p'}`;

    // Widgets that were previously visible but are absent from the new tile
    // set (i.e. removed via the per-tile "Remove" button — including the case
    // where the popup contained just a single card). These must be explicitly
    // sent to the backend as `is_visible: false`; without it, deleting the
    // last tile produces an empty payload and no API call is generated.
    const removedWidgetKeys = new Set<string>();
    for (const db of this.dashboards()) {
      for (const w of db.widgets) {
        if (!w.visible) continue;
        const key = widgetUniqueKey(db, w);
        if (!tileByUniqueKey.has(key)) {
          removedWidgetKeys.add(key);
        }
      }
    }

    this.dashboards.update((dbs) =>
      dbs.map((db) => ({
        ...db,
        widgets: db.widgets.map((w) => {
          const key = widgetUniqueKey(db, w);
          const tile = tileByUniqueKey.get(key);
          if (!tile) {
            return removedWidgetKeys.has(key) ? { ...w, visible: false } : w;
          }
          const newPosition = tileOrderByUniqueKey.get(key);
          const tileVisible = tile.visible !== false;
          return {
            ...w,
            colSpan: tile.colSpan,
            position: newPosition ?? w.position,
            visible: tileVisible,
          };
        }),
      })),
    );


    this.unifiedTilesOrder.set(tiles.map((t) => t.id));

    const uniqueDashboardIds = Array.from(
      new Set(
        this.dashboards()
          .map((d) => d.dashboardApiId)
          .filter((id): id is number => id != null),
      ),
    );

    if (uniqueDashboardIds.length === 0) {
      return;
    }

    this.layoutLoading.set(true);

    const dashboardsSnapshot = this.dashboards();
    const widgetLookupByUniqueKey = new Map<
      string,
      { dashboard: Dashboard; widget: Widget }
    >();
    for (const db of dashboardsSnapshot) {
      for (const w of db.widgets) {
        widgetLookupByUniqueKey.set(widgetUniqueKey(db, w), { dashboard: db, widget: w });
      }
    }

    const batchRequests = uniqueDashboardIds.map((dashboardId) => {
      const targetWidgets: Array<{
        widget_id: number;
        source_page_id: number | null;
        col_span: number;
        row_span: number;
        custom_config: Record<string, unknown>;
        is_visible: boolean;
      }> = [];
      const orderedWidgetIds: number[] = [];

      for (const t of tiles) {
        const found = widgetLookupByUniqueKey.get(t.id);
        if (!found) continue;
        const { dashboard: d, widget: w } = found;
        if (d.dashboardApiId !== dashboardId) continue;
        const tileVisible = t.visible !== false;
        const colSpan = t.colSpan ?? w.colSpan ?? 12;
        targetWidgets.push({
          widget_id: Number(w.widgetApiId),
          source_page_id: d.id.startsWith('api-page-')
            ? Number(d.id.replace('api-page-', ''))
            : null,
          col_span: colSpan,
          row_span: 1,
          // Preserve the widget's existing `custom_config` — sending `{}` here
          // would erase per-placement state the source page persisted on pin
          // (e.g. SEO Overview's selected platform `view`). The customize
          // popup only edits visibility / order / col-span, never the
          // per-widget config, so the stored value is the source of truth.
          custom_config: w.customConfig ?? {},
          is_visible: tileVisible,
        });

        if (tileVisible) {
          const widgetIdNum = Number(w.widgetApiId);
          if (Number.isFinite(widgetIdNum) && widgetIdNum > 0) {
            orderedWidgetIds.push(widgetIdNum);
          }
        }
      }

      // Append explicit `is_visible: false` rows for any widget removed from
      // the customize popup so the backend persists the deletion. This is the
      // piece that makes the API call happen when a user removes the only
      // remaining card (tiles array is empty, but `removedWidgetKeys` is not).
      for (const key of removedWidgetKeys) {
        const found = widgetLookupByUniqueKey.get(key);
        if (!found) continue;
        const { dashboard: d, widget: w } = found;
        if (d.dashboardApiId !== dashboardId) continue;
        targetWidgets.push({
          widget_id: Number(w.widgetApiId),
          source_page_id: d.id.startsWith('api-page-')
            ? Number(d.id.replace('api-page-', ''))
            : null,
          col_span: w.colSpan ?? 12,
          row_span: 1,
          // Keep the widget's `custom_config` even on remove so re-pinning
          // later (via the source page) rehydrates the same UI state.
          custom_config: w.customConfig ?? {},
          is_visible: false,
        });
      }

      const batch$ = this.layoutService
        .batchUpdateWidgets(dashboardId, { widgets: targetWidgets })
        .pipe(
          catchError((err) => {
            return of(null);
          }),
        );

      return batch$.pipe(
        switchMap(() => {
          if (orderedWidgetIds.length === 0) return of(null);
          return this.layoutService
            .putWidgetOrderImmediate(dashboardId, orderedWidgetIds)
            .pipe(
              catchError((err) => {
                return of(null);
              }),
            );
        }),
      );
    });

    forkJoin(batchRequests)
      .pipe(finalize(() => this.layoutLoading.set(false)))
      .subscribe(() => {
        this.toaster.success('Dashboard layout updated successfully!');
        this.loadCatalogData();
        this.reloadEmbeddedReports();
      });
  }

  /** Align manual tile order with API `position` after each catalog reload.
   * Always resets to an empty array first so that any stale keys from a
   * previous `persistCustomizeTiles` call cannot bleed into the new order.
   */
  private _syncUnifiedTilesOrderFromApi(): void {
    // Build the canonical order directly from the freshly-set dashboards state.
    // Reading visibleWidgets() here is safe because this method is always called
    // synchronously after dashboards.set() in the catalog subscribe handler.
    const order = this.visibleWidgets().map((t) => t.uniqueKey);
    this.unifiedTilesOrder.set(order);
  }

  private buildCustomizeTilesFromVisibleWidgets(): CustomizeDashboardPreviewTile[] | null {
    const tiles = this.unifiedTiles();
    if (!tiles.length) return null;

    return tiles.map((w) => {
      const source = this.customizeSourceChip(w.reportKey, w.pageName);

      const colSpan = normalizeCustomizeColSpan(this.resolveTileColSpan(w));

      return {
        id: w.uniqueKey,
        title: w.title,
        source,
        colSpan,
        // Forward the backend widget_key so the popup preview can detect
        // tall-tile cards (e.g. "Top Categories by Contracts" at col-span 8)
        // and render them with row-span-2, matching the live unified grid.
        widgetKey: w.id,
      };
    });
  }

  /**
   * Returns each tile mapped to its **designed default** col-span
   * (from {@link ON_SCREEN_COL_SPAN_BY_WIDGET_KEY}) — independent of any
   * width the user may have persisted via the customize popup.
   *
   * Used by the popup as the floor for the "decrease width" stepper: a card
   * cannot be shrunk below its designed default (e.g. a card designed for
   * `colSpan = 8` cannot be stepped down to 6 or 4).
   */
  private buildDefaultCustomizeTiles(): CustomizeDashboardPreviewTile[] | null {
    const tiles = this.unifiedTiles();
    if (!tiles.length) return null;

    return tiles.map((w) => {
      const source = this.customizeSourceChip(w.reportKey, w.pageName);
      const designedColSpan = w.id
        ? resolveUnifiedWidgetColSpan(w.id, ON_SCREEN_COL_SPAN_BY_WIDGET_KEY)
        : 12;
      const colSpan = normalizeCustomizeColSpan(designedColSpan);
      return {
        id: w.uniqueKey,
        title: w.title,
        source,
        colSpan,
        widgetKey: w.id,
      };
    });
  }

  navigateToReport(route: string) {
    // Normalize: protected routes live under `/app`, so prepend it if the caller forgot.
    const normalized = route.startsWith('/app')
      ? route
      : `/app${route.startsWith('/') ? '' : '/'}${route}`;
    this.router.navigate([normalized]);
  }
}
